<template>
  <CashCallCreateModalWrapper v-bind="$attrs" id="cashcallwrapper">
    <ValidationObserver v-slot="{ handleSubmit }" slim>
      <div
          class="form-container"
          :class="{ 'form-container--hscroll': currentStep === 2 && formData.is_co_lead }"
          ref="formContainer"
      >

        <form ref="cashCallForm" @submit.prevent="handleSubmit(onFinalSubmit)">
          <ErrorMessages :errors="errors" />
          <h5 class="modal-title">Cash Call Request Form</h5>
          <h4>
            Please fill out the form completely and upload required documents to
            submit.
          </h4>
          <!-- Progress Bar to track the % of form completion. -->
          <div class="progress-wrapper">
            <div class="progress">
              <div
                  class="progress-bar"
                  role="progressbar"
                  :style="{ width: progressPercent + '%' }"
              >
                {{ Math.round(progressPercent) }}%
              </div>
            </div>
          </div>
          <!-- Indicator to identify the current section of the form. -->
          <div class="step-indicator mb-1">
            <!-- <span
                v-for="(label, index) in stepLabels"
                :key="index"
                :class="['step', { active: currentStep === index + 1 }]"
            >
              {{ index + 1 }}. {{ label }}
            </span> -->
            <span
              v-for="(label, index) in stepLabels"
              :key="index"
              :class="[
                'step',
                {
                  active: currentStep === index + 1,
                  clickable: isHeaderClickable && (index + 1) <= maxVisitedStep,
                  disabled: isHeaderClickable && (index + 1) > maxVisitedStep
                }
              ]"
              @click="onHeaderClick(index + 1)"
            >
              {{ index + 1 }}. {{ label }}
            </span>
          </div>
          <div v-if="currentStep === 1">
            <ValidationObserver v-slot="{ handleSubmit }" ref="step1Observer">
              <!-- <p class="sectionHeader"><span>Initial Question Set</span></p> -->
              <b-row class="row">
                <b-col lg="6">
                  <CashCallEstablishedLossFund
                      v-model="formData.claim_loss_established"
                  />
                </b-col>
              </b-row>
              <b-row class="row">
                <b-col lg="6">
                  <CashCallReason v-model="formData.claim_reason" :show-errors="step1Attempted" />
                </b-col>
              </b-row>
              <b-row class="row">
                <b-col lg="6">
                  <BaseInput
                      v-model="formData.reason_manual"
                      v-if="formData.claim_reason == 'Other'"
                      :disabled="false"
                      :required="true"
                      rules="required"
                      label="Reason for Cash Call"
                      size="sm"
                  />
                </b-col>
              </b-row>
              <b-row class="row">
                <b-col lg="6">
                  <CashCallAuthorityScope
                      v-model="formData.claim_authority_scope"
                  />
                </b-col>
              </b-row>
              <b-row class="row align-items-start">
                <b-col lg="6">
                  <CashCallColeadQuestion v-model="formData.is_co_lead" />
                </b-col>
                <!-- Next button lives on the SAME row as the "Is this a Co-Lead
                     claim?" question so it's level with it (bottom-right of the
                     step), instead of dropping below the info box. It aligns
                     with the question's input via a small top offset. -->
                <b-col lg="6" class="d-flex justify-content-end step1-next-col">
                  <BaseButton type="button" @click="validateCurrentStep">
                    Next
                  </BaseButton>
                </b-col>
              </b-row>
              <br />
            </ValidationObserver>
          </div>
          <div v-if="currentStep === 2 && formData.is_co_lead">
            <LoadingPanel v-if="coLeadUmrCacheLoading" />
            <ValidationObserver v-else v-slot="{ handleSubmit }" ref="step2Observer">
              <CashCallCoLeadStep
                  v-model="formData.co_lead_keys"
                  :group-id="formData.group_id"
                  :search-index="coLeadSearchIndex"
              />
              <ValidationProvider
                  rules="required|min_value:100|max_value:100"
                  v-slot="{ errors }"
              >
                <input type="hidden" v-model.number="coLeadShareTotal" />
                <div v-if="errors.length" class="text-danger small mt-2">
                  Share percentages must add up to 100% before continuing (currently {{ coLeadShareTotal }}%).
                </div>
              </ValidationProvider>
            </ValidationObserver>
          </div>
          <div v-if="currentStep === 2 && !formData.is_co_lead">
            <ValidationObserver v-slot="{ handleSubmit }" ref="step2Observer">
              <!-- <p class="sectionHeader"><span>BDX Details</span></p> -->
              <b-row class="row">
                <b-col lg="6">
                  <CashCallBdxKeyStatus v-model="formData.bdx_key_exists" />
                </b-col>
              </b-row>
              <b-row class="row" v-if="formData.bdx_key_exists == true">
                <b-col lg="6">
                  <CashCallFormClaimExists v-model="formData.claim_exists" @input="onClaimExistsChange" />
                </b-col>
              </b-row>
              <b-row class="row">
                <b-col lg="4">
                  <BaseInput
                      v-model="formData.claim_ref_manual"
                      v-if="formData.bdx_key_exists == false || formData.claim_exists == false"
                      :disabled="false"
                      :required="formData.bdx_key_exists == false"
                      label="Claim Reference"
                      size="sm"
                  />
                  <CashCallFormClaimRef
                      v-if="formData.bdx_key_exists == true && formData.claim_exists == true"
                      v-model="formData.claimref"
                      :options="claimReferences"
                      @claimref-change="onClaimRefChange"
                  />
                </b-col>
                <b-col lg="5">
                  <!-- Manual path (LIQ-1006): the key is DERIVED from the six
                       BDX fields below via buildManualBdxKey(), so it stays
                       read-only - but it is no longer `required`, because a
                       disabled+required field can never satisfy the validator
                       and blocked "Save and Next" outright (LIQ-1196). The six
                       source fields carry the required rules instead. -->
                  <!-- Shows the key as a self-filling template
                       (UMR_RC_CC_SN_CUR_YOA_TPA_CH), each segment replaced as
                       its field is entered. The value PERSISTED is
                       formData.bdx_key, which carries Lincoln's '0' for unset
                       components - this box is display only. -->
                  <BaseInput
                      :value="manualBdxKeyDisplay"
                      v-if="formData.bdx_key_exists == false"
                      :disabled="true"
                      :required="false"
                      label="LimeIQ BDX Key"
                      size="sm"
                  />
                  <CashCallFormBdxList
                      v-if="formData.bdx_key_exists == true"
                      v-model="formData.bdx_key"
                      @update:selectedBdxKey="handleSelectedBdxKey"
                      @update:claimReferences="handleClaimReferences"
                  />
                </b-col>
              </b-row>
              <!-- LIQ-1006: manual BDX entry. When no BDX has been submitted
                   for this binder section, these six fields ARE the BDX key -
                   the user types them and buildManualBdxKey() composes
                   `bdx_key_manual` from them (see watcher below). On the
                   BDX-exists path they stay hidden and the read-only summary
                   card underneath shows the values parsed from the selected
                   key instead. -->
              <b-row class="row" v-if="formData.bdx_key_exists == false">
                <!-- UMR and Risk Code offer what the system already holds,
                     while still accepting a value that is not on the list
                     (can-add-new): a manual cash call may well be the first
                     time a UMR is seen here, which is the whole point of this
                     path. Cat Code and Section No stay free text - the values
                     behind them in existing keys are largely unvalidated
                     (dates in Cat Code, '---' and '(I)' in Section), so
                     offering them as choices would mislead rather than help. -->
                <b-col lg="2">
                  <CashCallFormManualUMR
                      v-model="formData.umr"
                      :user-company-id="userCompanyId"
                      :user-company-role="userCompanyRole"
                      :user-company-role-value="userCompanyRoleValue"
                      @update:keyPool="manualBdxKeyPool = $event"
                  />
                </b-col>
                <b-col lg="2">
                  <CashCallFormManualRiskCode v-model="formData.rc" />
                </b-col>
                <!-- Cat Code before Section No: the fields are laid out in the
                     same order they occupy in the composed key
                     (UMR_Risk_Cat_Section_Currency_YoA, per Lincoln's
                     create_new_key), so what the user types reads left-to-right
                     against the key shown above. -->
                <b-col lg="2">
                  <BaseInput
                      v-model="formData.cc"
                      :required="true"
                      label="Cat Code"
                      size="sm"
                  />
                </b-col>
                <b-col lg="2">
                  <BaseInput
                      v-model="formData.sn"
                      :required="true"
                      label="Section No"
                      size="sm"
                  />
                </b-col>
                <b-col lg="2">
                  <CashCallFormCurrency v-model="formData.currency_type" :disabled="false" />
                </b-col>
                <b-col lg="2">
                  <CashCallFormYoA v-model="formData.year_of_account" :disabled="false" />
                </b-col>
              </b-row>
              <b-card class="compact-summary" v-if="formData.bdx_key_exists != false">
                <table class="summary-table">
                  <tr>
                    <th>UMR</th>
                    <td>{{ formData.umr }}</td>

                    <th>Risk Code</th>
                    <td>{{ formData.rc }}</td>
                  </tr>

                  <tr>
                    <th>Section No</th>
                    <td>{{ formData.sn }}</td>

                    <th>Cat Code</th>
                    <td>{{ formData.cc }}</td>
                  </tr>

                  <tr>
                    <th>Currency Type</th>
                    <td>{{ formData.currency_type }}</td>

                    <th>Year of Account</th>
                    <td>{{ formData.year_of_account }}</td>
                  </tr>                  
                </table>
              </b-card>
              <br />
            </ValidationObserver>
          </div>
          <div v-if="currentStep === 3 && formData.is_co_lead">
            <ValidationObserver v-slot="{ handleSubmit }" ref="step3Observer">
              <CashCallCoLeadClaimDetailsStep
                  ref="coLeadClaimDetails"
                  v-model="formData.co_lead_keys"
                  :sanction-status="formData.sanction_status"
                  @update:sanctionStatus="formData.sanction_status = $event"
              />
            </ValidationObserver>
          </div>
          <div v-if="currentStep === 3 && !formData.is_co_lead">
            <ValidationObserver v-slot="{ handleSubmit }" ref="step3Observer">
              <b-row class="row">
                <b-col lg="3">
                  <CashCallFormSactionsCheckStatus
                      v-model="formData.sanction_status"
                  />
                </b-col>
                <b-col lg="3">
                  <CashCallFormCountry v-model="formData.loss_country" />
                </b-col>
                <b-col lg="3" v-if="formData.loss_country == 'Canada'">
                  <CashCallFormProvince v-model="formData.canada_province" searchable />
                </b-col>
                <b-col lg="2" v-if="formData.loss_country == 'USA'">
                  <CashCallFormState v-model="formData.loss_state" searchable v-if="!lossStateFromDb && (isValidStateCode || !formData.loss_state)" />
                  <BaseInput
                      v-else
                      v-model="formData.loss_state"                      
                      :disabled="true"
                      label="Loss State"
                      :required="true"
                      rules="required"
                      size="sm"
                  />
                </b-col>
                <b-col lg="2" v-if="formData.loss_country == 'USA'">
                  <CashCallFormTaxState v-model="formData.loss_sl_state" searchable v-if="!lossSLStateFromDb  && (isValidStateCode || !formData.loss_sl_state)" />
                  <BaseInput
                      v-else
                      v-model="formData.loss_sl_state"
                      :disabled="true"
                      label="SL State"
                      :required="true"
                      rules="required"
                      size="sm"
                  />
                  
                </b-col>
                <b-col lg="2" v-if="formData.loss_country == 'USA'">
                  <BaseInput
                      v-model="formData.naic_code"
                      :disabled="false"
                      label="NAIC Code"
                      :required="false"
                      size="sm"
                  />
                </b-col>
              </b-row>
              <b-row class="row">             
                <b-col lg="6">
                  <label for="cause_of_loss">Cause of Loss:</label> <br />
                  <!-- LIQ-1006 / LIQ-1754: Cause of Loss auto-populates from the
                       matched LimeIQ claim, so it stays read-only on that path.
                       When the claim is NOT in LimeIQ (no BDX submitted, or the
                       claim hasn't appeared on one) there is nothing to populate
                       it from and it must be typed - leaving it disabled is what
                       blocked the manual workflow entirely. -->
                  <b-form-textarea
                      id="cause_of_loss"
                      v-model="formData.cause_of_loss"
                      placeholder="Provide Brief Claim Description"
                      :disabled="!isManualClaimEntry"
                      rows="3"
                      max-rows="3"
                  ></b-form-textarea>
                </b-col>
              </b-row>
              <!-- <hr>
              <b-row class="row">
                <b-col lg="2">
                  <BaseInput
                      v-model="formData.insured_name"
                      :disabled="true"
                      :required="true"
                      rules="required"
                      label="Insured Name"
                      size="sm"
                  />
                </b-col>
                <b-col lg="2">
                  <BaseInput
                      v-model="formData.tpa"
                      :disabled="true"
                      label="TPA"
                      size="sm"
                  />
                </b-col>
                <b-col lg="2">
                  <BaseInput
                      v-model="formData.underwriter_lead"
                      :disabled="true"
                      :required="true"
                      label="Lead Underwriter"
                      size="sm"
                  />
                </b-col>
                <b-col lg="2">
                  <CashCallFormBroker v-if="showBrokerDropdown" v-model="formData.broker" />
                  <BaseInput
                      v-model="formData.broker"
                      v-else
                      :disabled="true"
                      :required="true"
                      label="Broker"
                      size="sm"
                  />
                </b-col>
                <b-col lg="2">
                  <BaseInput
                      v-model="formData.coverholder"
                      :disabled="true"
                      :required="true"
                      label="Coverholder"
                      size="sm"
                  />
                </b-col>
                <b-col lg="2">
                  <CashCallFormReportPeriodDate
                      v-model="formData.date_recent_report_period"
                      label="Recent Report Date"
                  />
                </b-col>
              </b-row>

              <b-row class="row">
                <b-col lg="3">
                  <CashCallFormLossDate
                      v-model="formData.date_of_loss"
                      label="Date of Loss"
                  />
                </b-col>               
                <b-col lg="3">
                  <CashCallFormClaimDate
                      v-model="formData.date_claim_made"
                      label="Date Claim Made"
                  />
                </b-col>
                <b-col lg="3">
                  <CashCallFormPolicyEffectiveDate
                      v-model="formData.date_risk_effective"
                  />
                </b-col>
                <b-col lg="3">
                  <CashCallFormPolicyExpiryDate
                      v-model="formData.date_risk_expiry"
                  />
                </b-col>
              </b-row>           -->
              <b-card class="compact-summary">
                <table class="summary-table">
                  <!-- LIQ-1006 / LIQ-1754: on the matched path these parties are
                       resolved from the BDX/claim record and shown as text. For a
                       claim not yet in LimeIQ there is nothing to resolve them
                       from, so Insured / TPA / Lead UW / Coverholder become
                       manually enterable - the "recipient fields must be manually
                       selectable" requirement. Broker already had its own dropdown
                       on both paths. -->
                  <tr>
                    <th>Insured</th>
                    <td>
                      <BaseInput
                          v-if="isManualClaimEntry"
                          v-model="formData.insured_name"
                          :required="true"
                          label=""
                          size="sm"
                      />
                      <template v-else>{{ formData.insured_name }}</template>
                    </td>

                    <th>TPA</th>
                    <td>
                      <!-- A TPA-role user's own company is forced server-side
                           (views.py auto-populates `tpa` from their role_value),
                           so they never pick one - show it as text for them. -->
                      <!-- label="" - the table's own row heading names the
                           field, so the dropdown must not repeat it. -->
                      <CashCallFormTPA
                          v-if="isManualClaimEntry && !isTpaRoleUser"
                          v-model="manualTpa"
                          label=""
                          @update:selectedTPAId="manualTpaId = $event"
                          @update:options="manualTpaOptions = $event"
                      />
                      <template v-else>{{ formData.tpa }}</template>
                    </td>
                  </tr>

                  <tr>
                    <th>Lead UW</th>
                    <td>
                      <CashCallFormUnderWriter
                          v-if="isManualClaimEntry"
                          v-model="manualUnderwriter"
                          label=""
                      />
                      <template v-else>{{ formData.underwriter_lead }}</template>
                    </td>

                    <th>Broker</th>
                    <td><template>
                      <CashCallFormBroker v-model="formData.broker" @update:selectedBrokerId="selectedBrokerId = $event" />
                    </template></td>

                    <!-- <template v-else>
                      {{ formData.broker }}
                    </template></td> -->
                  </tr>

                  <tr>
                    <th>Coverholder</th>
                    <td>
                      <CashCallFormCoverHolder
                          v-if="isManualClaimEntry"
                          v-model="manualCoverholder"
                          label=""
                          @update:selectedCoverholderId="manualCoverholderId = $event"
                          @update:options="manualCoverholderOptions = $event"
                      />
                      <template v-else>{{ formData.coverholder }}</template>
                    </td>

                    <th>Recent Report Date</th>
                    <!-- Editable on the manual path for the same reason the
                         parties above are: a claim raised before its BDX has
                         arrived has no dates to derive, so plain text would
                         leave them permanently unfillable. When the claim IS
                         already known these arrive prefilled and can still be
                         corrected. -->
                    <td>
                      <CashCallFormReportPeriodDate
                          v-if="isManualClaimEntry && !formData.date_recent_report_period"
                          v-model="formData.date_recent_report_period"
                          label=""
                          :disabled="false"
                      />
                      <template v-else>{{ formData.date_recent_report_period }}</template>
                    </td>
                  </tr>
                  <tr>
                    <th>Date of Loss</th>
                    <td>
                      <CashCallFormLossDate
                          v-if="isManualClaimEntry && !formData.date_of_loss"
                          v-model="formData.date_of_loss"
                          label=""
                          :disabled="false"
                      />
                      <template v-else>{{ formData.date_of_loss }}</template>
                    </td>

                    <th>Date Claim Made</th>
                    <td>
                      <CashCallFormClaimDate
                          v-if="isManualClaimEntry && !formData.date_claim_made"
                          v-model="formData.date_claim_made"
                          label=""
                          :disabled="false"
                      />
                      <template v-else>{{ formData.date_claim_made }}</template>
                    </td>
                  </tr>
                  <tr>
                    <th>Policy Effective Date</th>
                    <td>
                      <CashCallFormPolicyEffectiveDate
                          v-if="isManualClaimEntry && !formData.date_risk_effective"
                          v-model="formData.date_risk_effective"
                          label=""
                          :disabled="false"
                      />
                      <template v-else>{{ formData.date_risk_effective }}</template>
                    </td>

                    <th>Policy Expiry Date</th>
                    <td>
                      <CashCallFormPolicyExpiryDate
                          v-if="isManualClaimEntry && !formData.date_risk_expiry"
                          v-model="formData.date_risk_expiry"
                          label=""
                          :disabled="false"
                      />
                      <template v-else>{{ formData.date_risk_expiry }}</template>
                    </td>
                  </tr>
                </table>
              </b-card>

            </ValidationObserver>
          </div>
          <div v-if="currentStep === 4">
            <ValidationObserver v-slot="{ handleSubmit }" ref="step4Observer">
              <!-- <p class="sectionHeader"><span>Financial Details</span></p> -->
               <!-- 🔹 NEW FINANCE FIELDS - SIDE BY SIDE -->
              <b-row class="row mb-2" v-if="!formData.is_co_lead">
                <b-col lg="4">
                  <BaseInputCurrency
                    v-model="formData.initial_loss_fund_value"
                    :disabled="false"
                    :displayType="displayflex"
                     rules=""
                    label="Initial Loss Fund Value"
                    size="sm"
                  />
                </b-col>

                <b-col lg="4">
                  <BaseInputCurrency
                    v-model="formData.current_loss_fund_balance"
                    :disabled="false"
                    :displayType="displayflex"
                     rules=""
                    label="Current Loss Fund Balance"
                    size="sm"
                  />
                </b-col>
              </b-row>

              <CashCallCoLeadLossFundTabs
                  v-if="formData.is_co_lead"
                  v-model="formData.co_lead_keys"
                  class="mb-3"
              />

              <b-row class="row">
                <b-col lg="5">
                  <CashCallFeeExists v-model="formData.fee_exists" />
                </b-col>
              </b-row> <br />
              <b-row class="row">
                <b-col lg="8">
                  <BaseInputCurrency
                      v-model="formData.total_indemnity"
                      :disabled="false"
                      :displayType="displayflex"
                      rules="required"
                      label="Indemnity"
                      size="sm"
                  />
                </b-col>
              </b-row>
              <b-row class="row" v-if="formData.fee_exists == true">
                <b-col lg="8">
                  <BaseInputCurrency
                      v-model="formData.tpa_fees_amount"
                      :disabled="false"
                      :displayType="displayflex"
                      rules="required"
                      label="TPA Fees"
                      size="sm"
                  />
                </b-col>
              </b-row>
              <b-row class="row" v-if="formData.fee_exists == true">
                <b-col lg="8">
                  <BaseInputCurrency
                      v-model="formData.adjuster_fee_amount"
                      :disabled="false"
                      :displayType="displayflex"
                      rules="required"
                      label="Adjusters Fees"
                      size="sm"
                  />
                </b-col>
              </b-row>
              <b-row class="row" v-if="formData.fee_exists == true">
                <b-col lg="8">
                  <BaseInputCurrency
                      v-model="formData.defence_fees_amount"
                      :disabled="false"
                      :displayType="displayflex"
                      rules="required"
                      label="Defence Fees"
                      size="sm"
                  />
                </b-col>
              </b-row>
              <b-row class="row" v-if="formData.fee_exists == true">
                <b-col lg="8">
                  <BaseInputCurrency
                      v-model="formData.expenses_amount"
                      :disabled="false"
                      :displayType="displayflex"
                      rules="required"
                      label="Expenses"
                      size="sm"
                  />
                </b-col>
              </b-row>
              <b-row class="row" v-if="formData.fee_exists == true">
                <b-col lg="8">
                  <BaseInputCurrency
                      v-model="formData.attorney_coverage_amount"
                      :disabled="false"
                      :displayType="displayflex"
                      rules="required"
                      label="Attorney Coverage"
                      size="sm"
                  />
                </b-col>
              </b-row>
              <b-row class="row" v-if="formData.fee_exists == true">
                <b-col lg="8">
                  <BaseInputCurrency
                      v-model="formData.total_fee"
                      :disabled="true"
                      :displayType="displayflex"
                      rules="required"
                      label="Total Fees"
                      size="sm"
                  />
                </b-col>
              </b-row>
              <b-row class="row">
                <b-col lg="8">
                  <BaseInputCurrency
                      v-model="formData.amount"
                      :disabled="true"
                      :displayType="displayflex"
                      rules="required"
                      label="Total Cash Call Request"
                      size="sm"
                  />
                </b-col>
              </b-row>

              <!-- Statutory Deadline: optional date logged at the payment stage
                   (Stage 4). Populate only where a statutory required deadline
                   exists. Shown for both normal and co-lead flows. -->
              <b-row class="row mt-2">
                <b-col lg="4">
                  <label class="statutory-deadline-label">
                    Statutory Deadline
                    <b-icon
                        icon="info-circle"
                        v-b-tooltip.hover="'Please populate where necessary if there is a statutory required deadline.'"
                    />
                  </label>
                  <b-form-input
                      v-model="formData.statutory_deadline"
                      type="date"
                      size="sm"
                  />
                </b-col>
              </b-row>

              <!-- Co-lead Financial Summary is shown on the Review Summary
                   (step 6), so it's not duplicated here on step 4. -->

              <br />
            </ValidationObserver>
          </div>
          <div v-if="currentStep === 5">
            <h3 class="review-title mb-2">Supporting Documentation</h3>

            <ValidationObserver v-slot="{ handleSubmit }" ref="step5Observer">
              <div class="doc-list">

               <!-- CHECKBOX GRID (3 per row) -->
                <div class="checkbox-grid">
                  <div
                    v-for="doc in documentationOptions"
                    :key="doc"
                    class="checkbox-item"
                  >
                    <input
                      type="checkbox"
                      :value="doc"
                      v-model="selectedDocs"
                      @change="onDocCheckboxChange(doc)"
                      class="mr-2"
                    />

                    <!-- Label for normal docs -->
                    <span v-if="doc !== 'Other'">
                      {{ doc }}
                    </span>

                    <!-- Other input -->
                    <span v-if="doc === 'Other'" style="width:100%;">
                      <input
                        type="text"
                        v-model="otherDescription"
                        placeholder="Other – Please Specify"
                        class="form-control mt-1"
                        maxlength="40"
                        :class="{'is-invalid': otherDescription.length > 40}"
                        @change="updateDocumentationOption"
                        :disabled="!selectedDocs.includes('Other')"
                      />
                      <span v-if="otherDescription.length > 40" class="invalid-feedback">
                        Input exceeds 40 characters!
                      </span>
                    </span>
                  </div>
                </div>


                <hr />

                <!-- UPLOAD SECTIONS VISIBLE ONLY WHEN CHECKBOX IS SELECTED -->
                <div
                  v-for="doc in documentationOptions"
                  :key="'upload_' + doc"
                  class="doc-row mb-3"
                  v-if="selectedDocs.includes(doc)"
                >
                  <div class="d-flex align-items-center" style="padding-bottom: 10px;">

                    <!-- Display label -->
                    <span class="doc-label" style="width:350px">
                      {{ doc === 'Other' ? otherDescription || 'Other' : doc }}
                    </span>

                    <!-- Upload Button -->
                    <div class="d-flex1 align-items-center">
                      <BaseButton
                        size="sm"
                        type="button"
                        class="mr-2"
                        @click="triggerUpload(doc)"
                      >
                        Upload
                      </BaseButton>
                      <br />

                      <!-- Spinner -->
                      <div v-if="uploadingDocuments[doc]" class="uploading-spinner">
                        <b-icon icon="clock" spin></b-icon>
                      </div>

                      <!-- Uploaded files -->
                      <div
                        v-if="uploadedDocuments[doc] && uploadedDocuments[doc].length"
                        class="uploaded-files mt-2"
                      >
                        <div
                          v-for="(obj, index) in uploadedDocuments[doc]"
                          :key="obj.file.name + index"
                          class="uploaded-file-item d-flex justify-content-between align-items-center"
                        >
                          <span>{{ obj.file.name }}</span> &nbsp;

                          <!-- Spinner while deleting -->
                          <div v-if="deletingFile === `${doc}_${obj.file.name}`">
                            <b-spinner small type="border" variant="danger"></b-spinner>
                            <span class="ml-2">Deleting...</span>
                          </div>

                          <!-- Delete icon -->
                          <button
                            type="button"
                            @click="deleteUploadedFile(doc, index)"
                            v-if="deletingFile !== `${doc}_${obj.file.name}`"
                          >
                            <b-icon icon="x-circle" variant="danger"></b-icon>
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Hidden input -->
                    <input
                      type="file"
                      :ref="'file_' + doc"
                      class="d-none"
                      @change="handleFileUpload($event, doc, 3)"
                      multiple
                      accept=".xls,.xlsx,.pdf,.doc,.docx"
                    />
                  </div>
                </div>
              </div>
            </ValidationObserver>
          </div>

          <!-- Review Summary Section Begin -->
          <div v-if="currentStep === 6" class="review-container">
            <ValidationObserver v-slot="{ handleSubmit }" ref="step6Observer">
              <h3 class="review-title mb-4">Review Your Information</h3>

              <b-card no-body>
                <b-card-body class="animated-section">

                  <!-- ACCORDION -->
                  <b-accordion id="summary-accordion">

                    <!-- SECTION 1: Initial Questions -->
                    <b-card no-body class="mb-2">
                      <b-card-header header-tag="header" class="accordion-header" role="tab">
                        <div
                            v-b-toggle.initial-acc
                            class="accordion-trigger d-flex align-items-center"
                        >
                          <i class="chevron ml-2" :class="{ rotated: activeAccordion === 'initial-acc' }"></i> &nbsp;&nbsp;
                          <span class="section-title">Initial Questions</span>
                        </div>

                        <BaseButton
                            size="sm"
                            type="button"
                            class="edit-btn ml-auto"
                            @click="jumpToStep(1)"
                        >
                          Edit
                        </BaseButton>
                      </b-card-header>

                      <b-collapse
                          id="initial-acc"
                          accordion="summary-accordion"
                          @show="onAccordionShow('initial-acc')"
                          @hide="onAccordionHide('initial-acc')"
                      >
                        <b-card-body>
                          <div v-if="formData.is_co_lead" class="review-row"><span class="label">Co-Lead claim?</span><span class="value">Yes</span></div>
                          <template v-else>
                            <div class="review-row"><span class="label">Loss Fund Established?</span><span class="value">{{ formData.claim_loss_established == true ? 'Yes': 'No' }}</span></div>
                            <div class="review-row"><span class="label">Claim Reason</span><span class="value">{{ formData.claim_reason === 'Other' ? formData.reason_manual : formData.claim_reason }}</span></div>
                            <div class="review-row"><span class="label">Above Authority Payment?</span><span class="value">{{ formData.claim_authority_scope == true ? 'Yes': 'No'  }}</span></div>
                          </template>
                        </b-card-body>
                      </b-collapse>
                    </b-card>

                    <!-- SECTION 2: BDX DETAILS -->
                    <b-card no-body class="mb-2">

                      <b-card-header header-tag="header" class="accordion-header" role="tab">
                        <div
                            v-b-toggle.bdx-acc
                            class="accordion-trigger d-flex align-items-center"
                        >
                          <i class="chevron ml-2" :class="{ rotated: activeAccordion === 'bdx-acc' }"></i> &nbsp;&nbsp;
                          <span class="section-title">{{ formData.is_co_lead ? 'UMR Selection &amp; Shares' : 'BDX Details' }}</span>
                        </div>

                        <BaseButton
                            size="sm"
                            type="button"
                            class="edit-btn ml-auto"
                            @click="jumpToStep(2)"
                        >
                          Edit
                        </BaseButton>
                      </b-card-header>

                      <b-collapse
                          id="bdx-acc"
                          accordion="summary-accordion"
                          @show="onAccordionShow('bdx-acc')"
                          @hide="onAccordionHide('bdx-acc')"
                      >
                        <b-card-body>
                          <!-- CO-LEAD: UMR Selection & Shares -->
                          <template v-if="formData.is_co_lead">
                            <div class="co-lead-summary-card">
                              <div class="co-lead-summary-card__item">
                                <div class="co-lead-summary-card__label">TPA(s)</div>
                                <div class="co-lead-summary-card__value">{{ coLeadTpas }}</div>
                              </div>
                              <div class="co-lead-summary-card__item">
                                <div class="co-lead-summary-card__label">Coverholder</div>
                                <div class="co-lead-summary-card__value">{{ coLeadCoverholders }}</div>
                              </div>
                              <div class="co-lead-summary-card__item">
                                <div class="co-lead-summary-card__label">Active UMRs</div>
                                <div class="co-lead-summary-card__value">{{ coLeadActiveUmrCount }}</div>
                              </div>
                              <div class="co-lead-summary-card__item">
                                <div class="co-lead-summary-card__label">Non-LIQ (internal)</div>
                                <div class="co-lead-summary-card__value">{{ coLeadNonLiqCount }}</div>
                              </div>
                              <div class="co-lead-summary-card__item">
                                <div class="co-lead-summary-card__label">Exports</div>
                                <div class="co-lead-summary-card__value">{{ coLeadExportsCount }}</div>
                              </div>
                            </div>

                            <b-table-simple small class="mt-3 co-lead-review-table">
                              <b-thead>
                                <b-tr>
                                  <b-th>UMR</b-th>
                                  <b-th>Underwriter</b-th>
                                  <b-th>Broker</b-th>
                                  <b-th>Share %</b-th>
                                  <b-th>Funding req.</b-th>
                                </b-tr>
                              </b-thead>
                              <b-tbody>
                                <b-tr
                                    v-for="row in formData.co_lead_keys"
                                    :key="row.bdx_key[0]"
                                    :class="{ 'co-lead-review-table__row--no-funding': row.funding_required === false }"
                                >
                                  <b-td>{{ row.umr }}</b-td>
                                  <b-td>{{ row.underwriter_lead }}</b-td>
                                  <b-td>{{ Array.isArray(row.broker) ? row.broker.join(', ') : row.broker }}</b-td>
                                  <b-td>{{ row.share_percentage }}%</b-td>
                                  <b-td>
                                    <span class="co-lead-review-table__badge" :class="{ 'co-lead-review-table__badge--no': row.funding_required === false }">
                                      {{ row.funding_required === false ? 'No' : 'Yes' }}
                                    </span>
                                  </b-td>
                                </b-tr>
                              </b-tbody>
                            </b-table-simple>
                          </template>

                          <!-- SINGLE-KEY: BDX Details -->
                          <template v-else>
                            <div class="review-row"><span class="label">BDX Submitted to LimeIQ</span><span class="value">{{ formData.bdx_key_exists == true ? 'Yes': 'No' }}</span></div>
                            <div class="review-row"><span class="label">Claim appeared on the LimeIQ submitted BDX</span><span class="value">{{ formData.claim_exists == true ? 'Yes': 'No' }}</span></div>
                            <div class="review-row"><span class="label">BDX Key</span><span class="value">{{ formData.bdx_key[0] }}</span></div>
                            <!-- LIQ-1006: a manually entered key is not backed by BDX data
                                 yet. Flag it here so the user knows the claim/BDX details
                                 they typed will be superseded once the BDX arrives (their
                                 Cash Call financials are not). -->
                            <div class="review-row" v-if="isManualClaimEntry">
                              <span class="label">BDX Confirmation</span>
                              <span class="value text-warning">Pending BDX Confirmation</span>
                            </div>
                            <div class="review-row"><span class="label">UMR</span><span class="value">{{ formData.umr }}</span></div>
                            <div class="review-row"><span class="label">Risk Code</span><span class="value">{{ formData.rc }}</span></div>
                            <div class="review-row"><span class="label">Section No</span><span class="value">{{ formData.sn }}</span></div>
                            <div class="review-row"><span class="label">Cat Code</span><span class="value">{{ formData.cc }}</span></div>
                            <div class="review-row"><span class="label">Settlement Currency</span><span class="value">{{ formData.currency_type }}</span></div>
                            <div class="review-row"><span class="label">Year of Account</span><span class="value">{{ formData.year_of_account }}</span></div>
                          </template>
                        </b-card-body>
                      </b-collapse>
                    </b-card>

                    <!-- SECTION 3: CLAIM DETAILS -->
                    <b-card no-body class="mb-2">

                      <b-card-header header-tag="header" class="accordion-header" role="tab">
                        <div
                            v-b-toggle.claim-acc
                            class="accordion-trigger d-flex align-items-center"
                        >
                          <i class="chevron ml-2" :class="{ rotated: activeAccordion === 'claim-acc' }"></i> &nbsp;&nbsp;
                          <span class="section-title">Claim Details</span>
                        </div>

                        <BaseButton
                            size="sm"
                            type="button"
                            class="edit-btn ml-auto"
                            @click="jumpToStep(3)"
                        >
                          Edit
                        </BaseButton>
                      </b-card-header>

                      <b-collapse
                          id="claim-acc"
                          accordion="summary-accordion"
                          @show="onAccordionShow('claim-acc')"
                          @hide="onAccordionHide('claim-acc')"
                      >
                        <b-card-body>
                          <!-- CO-LEAD: Claim Details are shared across the whole
                               co-lead (ONE claim), so show a SINGLE set - not one
                               card per UMR (which duplicated identical values).
                               Read from the first key, which carries the shared
                               claim values (inherited to every key). -->
                          <div v-if="formData.is_co_lead && coLeadClaimSummary">
                            <div class="co-lead-claim-card mb-2">
                              <div class="co-lead-claim-card__grid">
                                <div class="review-row"><span class="label">Insured</span><span class="value">{{ coLeadClaimSummary.insured_name || '—' }}</span></div>
                                <div class="review-row"><span class="label">Sanctions Check</span><span class="value">{{ formData.sanction_status || '—' }}</span></div>
                                <div class="review-row"><span class="label">Cause of Loss</span><span class="value">{{ coLeadClaimSummary.cause_of_loss || '—' }}</span></div>
                                <div class="review-row"><span class="label">Loss State</span><span class="value">{{ coLeadClaimSummary.loss_state || '—' }}</span></div>
                                <div class="review-row"><span class="label">Claim Country</span><span class="value">{{ coLeadClaimSummary.loss_country || '—' }}</span></div>
                                <div class="review-row"><span class="label">SL Tax State</span><span class="value">{{ coLeadClaimSummary.loss_sl_state || '—' }}</span></div>
                                <div class="review-row"><span class="label">NAIC Code</span><span class="value">{{ coLeadClaimSummary.naic_code || '—' }}</span></div>
                              </div>
                            </div>
                          </div>

                          <!-- SINGLE-KEY: Claim Details -->
                          <template v-else>
                            <div class="review-row"><span class="label">Claim Reference</span><span class="value">{{ formData.claimref[0] }}</span></div>
                            <div class="review-row"><span class="label">Insured Name</span><span class="value">{{ formData.insured_name }}</span></div>
                            <div class="review-row"><span class="label">Date of Loss</span><span class="value">{{ formData.date_of_loss }}</span></div>
                            <div class="review-row"><span class="label">Claim Made</span><span class="value">{{ formData.date_claim_made }}</span></div>
                            <div class="review-row"><span class="label">Policy Risk Effective Date</span><span class="value">{{ formData.date_risk_effective }}</span></div>
                            <div class="review-row"><span class="label">Policy Risk Expiration Date</span><span class="value">{{ formData.date_risk_expiry }}</span></div>
                            <div class="review-row"><span class="label">TPA</span><span class="value">{{ formData.tpa }}</span></div>
                            <div class="review-row"><span class="label">Lead Underwriter</span><span class="value">{{ formData.underwriter_lead }}</span></div>
                            <div class="review-row"><span class="label">Broker</span><span class="value">{{ brokerDisplay }}</span></div>
                            <div class="review-row"><span class="label">Coverholder</span><span class="value">{{ formData.coverholder }}</span></div>
                            <div class="review-row"><span class="label">Claim Country</span><span class="value">{{ formData.loss_country }}</span></div>
                            <div class="review-row"><span class="label">NAIC Code</span><span class="value">{{ formData.naic_code }}</span></div>
                          </template>
                        </b-card-body>
                      </b-collapse>
                    </b-card>

                    <!-- SECTION 4: FINANCIAL DETAILS -->
                    <b-card no-body class="mb-2">

                      <b-card-header header-tag="header" class="accordion-header" role="tab">
                        <div
                            v-b-toggle.fin-acc
                            class="accordion-trigger d-flex align-items-center"
                        >
                          <i class="chevron ml-2" :class="{ rotated: activeAccordion === 'fin-acc' }"></i> &nbsp;&nbsp;
                          <span class="section-title">Financial Details</span>
                        </div>

                        <BaseButton
                            size="sm"
                            type="button"
                            class="edit-btn ml-auto"
                            @click="jumpToStep(4)"
                        >
                          Edit
                        </BaseButton>
                      </b-card-header>

                      <!-- Financial summary is open by DEFAULT on Step 6 (it no
                           longer appears on Step 4, so Review is where the user
                           sees the financials). -->
                      <b-collapse
                          id="fin-acc"
                          visible
                          accordion="summary-accordion"
                          @show="onAccordionShow('fin-acc')"
                          @hide="onAccordionHide('fin-acc')"
                      >
                        <b-card-body>
                          <!-- CO-LEAD: reuse the shared financial summary from step 4 -->
                          <CashCallCoLeadFinancialSummary
                              v-if="formData.is_co_lead"
                              :rows="formData.co_lead_keys"
                              :total-cash-call-amount="Number(formData.amount) || 0"
                          />

                          <!-- SINGLE-KEY: Financial Details -->
                          <template v-else>
                            <div class="review-row"><span class="label">Total Fees</span><span class="value">${{ formData.total_fee.toLocaleString('en-US') }}</span></div>
                            <div class="review-row"><span class="label">Indemnity</span><span class="value">${{ formData.total_indemnity.toLocaleString('en-US') }}</span></div>
                            <div class="review-row"><span class="label">Cash Call Amount</span><span class="value amount">${{ formData.amount.toLocaleString('en-US') }}</span></div>
                          </template>
                        </b-card-body>
                      </b-collapse>
                    </b-card>

                    <!--  SECTION 5: Supporting Documents -->
                    <b-card no-body class="mb-2">
                      <b-card-header header-tag="header" class="accordion-header" role="tab">
                        <div
                            v-b-toggle="'docs-acc'"
                            class="accordion-trigger d-flex align-items-center"
                        >
                          <i class="chevron ml-2" :class="{ rotated: activeAccordion === 'docs-acc' }"></i> &nbsp;&nbsp;
                          <span class="section-title">Supporting Documents</span>
                        </div>
                        <BaseButton
                            size="sm"
                            type="button"
                            class="edit-btn ml-auto"
                            @click="jumpToStep(5)"
                        >
                          Edit
                        </BaseButton>
                      </b-card-header>

                      <b-collapse
                          id="docs-acc"
                          accordion="summary-accordion"
                          role="tabpanel"
                          @show="onAccordionShow('docs-acc')"
                          @hide="onAccordionHide('docs-acc')"
                      >
                        <b-card-body class="animated-section">

                          <!-- CO-LEAD: no documents attached -->
                          <div
                              v-if="formData.is_co_lead && !hasUploadedDocuments"
                              class="text-danger font-weight-bold"
                          >
                            No documents attached.
                          </div>

                          <div
                              v-for="doc in documentationOptions"
                              v-else
                              :key="doc"
                              class="review-row"
                          >
                            <div class="d-flex justify-content-between w-100">
                              <span class="label">{{ doc }}</span>

                              <!-- Show ✔ or "No Files" -->
                              <span class="value" v-if="uploadedDocuments[doc] && uploadedDocuments[doc].length > 0">
                            <label class="files-label">✔ {{ uploadedDocuments[doc].length }} file(s) </label>
                                <!-- List files -->
                            <ul v-if="uploadedDocuments[doc] && uploadedDocuments[doc].length > 0" class="uploaded-summary">
                              <li
                                  v-for="obj in uploadedDocuments[doc]"
                                  :key="obj.file.name"
                              >
                                {{ obj.file.name }}
                              </li>
                            </ul>
                          </span>
                              <span class="value text-muted" v-else>
                            No files uploaded
                            <span
                              v-if="selectedDocs.includes(doc)"
                              class="text-danger ml-2"
                              style="font-weight: bold;"
                            >
                              ⚠ Document selected but not uploaded
                            </span>
                          </span>
                            </div>
                          </div>

                        </b-card-body>
                      </b-collapse>
                    </b-card>


                  </b-accordion>

                  <!-- Cash Call Comments -->
                  <ValidationProvider
                    name="Cash Call Comments"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <div class="mt-4">
                      <label class="limeiq-label">
                        Cash Call Comments <span class="text-danger">*</span>
                      </label>

                      <textarea
                        v-model="formData.cash_call_comments"
                        class="limeiq-textarea"
                        rows="4"
                        maxlength="1000"
                        placeholder="Enter comments that will appear in the Cash Call email and PDF"
                      ></textarea>

                      <div class="invalid-feedback d-block" v-if="errors[0]">
                        {{ errors[0] }}
                      </div>
                    </div>
                  </ValidationProvider>

                </b-card-body>
              </b-card>
            </ValidationObserver>
          </div>
          <!-- Review Summary Section End -->

          <!-- CO-LEAD email flow (prompt + recipient modal + send) lives in the
               parent CashCallDetails so it survives this wizard closing. On
               "Compile Cash Call" we emit `cashcall:colead-compiled` and close. -->

          <!-- Steps 2+ footer: Previous on the left, the action on the right.
               (Step 1's Next lives inline beside the "Is this a Co-Lead claim?"
               question above, so it's level with it, and is hidden here.) -->
          <div v-if="currentStep !== 1" class="d-flex justify-content-between">

            <!-- Previous Button -->
            <BaseButton v-if="currentStep > 1" type="button" @click="prevStep">
              Previous
            </BaseButton>

            <!-- Steps 2-4: Save and Next -->
            <span
              v-if="currentStep >= 2 && currentStep <= 4"
              class="save-next-wrap d-inline-flex align-items-center"
            >
              <!-- Why the button is disabled: co-lead share total isn't 100%.
                   Hover the info icon (LEFT of the button) for a simple hint. -->
              <b-icon
                v-if="currentStep === 2 && formData.is_co_lead && coLeadShareTotal !== 100"
                icon="info-circle"
                class="save-next-info mr-2"
                v-b-tooltip.hover="{ customClass: 'save-next-tooltip' }"
                title="Share percentages must add up to 100%"
              />

              <BaseButton
                type="button"
                :disabled="savingDraft || (currentStep === 2 && (coLeadUmrCacheLoading || (formData.is_co_lead && coLeadShareTotal !== 100)))"
                @click="onSaveAndNext"
              >
                <template v-if="savingDraft">
                  <b-spinner small type="grow"></b-spinner>
                  Saving...
                </template>

                <template v-else>
                  Save and Next
                </template>
              </BaseButton>
            </span>

            <!-- Step 5: Normal Next (step 1's Next is inline with the Co-Lead
                 question above, so it's excluded here). -->
            <BaseButton
              v-if="currentStep == 5"
              type="button"
              @click="validateCurrentStep"
            >
              Next
            </BaseButton>

            <!-- Co-Lead Review (step 6, final step): compile the cash call, close
                 the wizard and raise the "Email Cash Call Now?" prompt (mirrors
                 the single-key Compile Cash Call flow). -->
            <BaseButton
              v-if="formData.is_co_lead && currentStep === 6"
              :disabled="savingDraft"
              type="button"
              @click="onColeadCompile"
            >
              <template v-if="savingDraft">
                <b-spinner small type="grow"></b-spinner> Compiling...
              </template>
              <template v-else>Compile Cash Call</template>
            </BaseButton>

            <!-- Final Step Submit (single-key only; co-lead step 7 uses the Send modal) -->
            <BaseButton
              :disabled="loading"
              type="button"
              v-if="!formData.is_co_lead && currentStep === totalSteps"
              @click="onFinalSubmit"
            >
              <template v-if="loading">
                <b-spinner small type="grow"></b-spinner> Submitting...
              </template>
              <template v-else>Compile Cash Call</template>
            </BaseButton>

          </div>
        </form>
        <div class="form-overlay" v-if="loading">
          <b-spinner
              label="Loading..."
              variant="light"
              class="spinner-lg"
          ></b-spinner>
        </div>
      </div>
    </ValidationObserver>
  </CashCallCreateModalWrapper>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  computed,
  ref,
  provide,
  onMounted,
  watch,
  nextTick,
  markRaw,
  getCurrentInstance
} from '@vue/composition-api'
import { useToast, useCurrentUser } from '@composables'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { createVuexHelpers } from 'vue2-helpers'
import CashCallCreateModalWrapper from './CashCallCreateModalWrapper.vue'
import { BaseInputCurrency, BaseInput, BaseButton } from '@components/base'
import { ErrorMessages } from '@components/misc'
import CashCallFormClaimRef from './CashCallFormClaimRef.vue'
import CashCallFormUMR from './CashCallFormUMR.vue'
import { CashCallFormInjectionKey } from './CashCallForm.constants'
import CashCallFormRiskCode from './CashCallFormRiskCode.vue'
// LIQ-1006 manual path only. Distinct from CashCallFormUMR / CashCallFormRiskCode
// above, which pick from the BDX keys the user already has on the "Yes" path;
// these two suggest from the system while still accepting a new value.
import CashCallFormManualUMR from './CashCallFormManualUMR.vue'
import CashCallFormManualRiskCode from './CashCallFormManualRiskCode.vue'
import CashCallFormSectionNo from './CashCallFormSectionNo.vue'
import CashCallCatCode from './CashCallFormCatCode.vue'
import CashCallFormCatCode from './CashCallFormCatCode.vue'
import CashCallFormCoverHolder from './CashCallFormCoverHolder.vue'
import CashCallFormTPA from './CashCallFormTPA.vue'
import CashCallFormUnderWriter from './CashCallFormUnderWriter.vue'
import CashCallFormBroker from './CashCallFormBroker.vue'
import CashCallFormLossDate from './CashCallFormLossDate.vue'
import CashCallFormCurrency from './CashCallFormCurrency.vue'
import CashCallFormTaxState from './CashCallFormTaxState.vue'
import CashCallFormReportDate from './CashCallFormPolicyEffectiveDate.vue'
import CashCallEstablishedLossFund from './CashCallEstablishedLossFund.vue'
import CashCallAuthorityScope from './CashCallAuthorityScope.vue'
import CashCallReason from './CashCallReason.vue'
import CashCallBdxKeyStatus from './CashCallBdxKeyStatus.vue'
import CashCallColeadQuestion from './CashCallColeadQuestion.vue'
import CashCallUploadDocumentType from './CashCallUploadDocumentType.vue'
import CashCallFormYoA from './CashCallFormYoA.vue'
import CashCallFormClaimDate from './CashCallFormClaimDate.vue'
import CashCallFormPolicyEffectiveDate from './CashCallFormPolicyEffectiveDate.vue'
import CashCallFormPolicyExpiryDate from './CashCallFormPolicyExpiryDate.vue'
import CashCallFormCountry from './CashCallFormCountry.vue'
import CashCallFormState from './CashCallFormState.vue'
import CashCallFormSactionsCheckStatus from './CashCallFormSactionsCheckStatus.vue'
import CashCallFormProvince from './CashCallFormProvince.vue'
import CashCallFeeExists from './CashCallFeeExists.vue'
import CashCallDocumentUploader from './CashCallDocumentUploader.vue'
import CashCallFormBdxList from './CashCallFormBdxList.vue'
import { userMixin } from '@mixins'
import CashCallService from '../../services/CashCallService'
import CashCallFormClaimExists from './CashCallFormClaimExists.vue'
import CashCallFormReportPeriodDate from './CashCallFormReportPeriodDate.vue'
import  { LincolnRLSGroupsService }  from '@services'
import { CASH_CALL_REASON_OPTIONS, CASH_CALL_SUPPORTING_DOCUMENT_OPTIONS } from '@constants'
import CashCallCoLeadStep from './CashCallCoLeadStep.vue'
import CashCallCoLeadClaimDetailsStep from './CashCallCoLeadClaimDetailsStep.vue'
import CashCallCoLeadLossFundTabs from './CashCallCoLeadLossFundTabs.vue'
import CashCallCoLeadFinancialSummary from './CashCallCoLeadFinancialSummary.vue'
import LoadingPanel from '../LoadingPanel.vue'
import { totalSharePercentage, flattenUmrClaimsCache, buildSearchIndex, buildManualBdxKey } from '../../util/cashCallCoLead'


export default defineComponent({
  name: 'CashCallCreateModal',
  mixins: [userMixin],
  components: {
    ValidationObserver,
    ValidationProvider,
    CashCallCreateModalWrapper,
    CashCallFormClaimRef,
    CashCallFormUMR,
    CashCallFormManualUMR,
    CashCallFormManualRiskCode,
    CashCallFormCatCode,
    CashCallFormRiskCode,
    CashCallFormSectionNo,
    CashCallFormTPA,
    CashCallFormBroker,
    CashCallFormCoverHolder,
    CashCallFormUnderWriter,
    CashCallFormLossDate,
    CashCallFormReportDate,
    CashCallFormCurrency,
    CashCallFormTaxState,
    CashCallEstablishedLossFund,
    CashCallAuthorityScope,
    CashCallReason,
    CashCallBdxKeyStatus,
    CashCallColeadQuestion,
    CashCallCoLeadStep,
    CashCallCoLeadClaimDetailsStep,
    CashCallCoLeadLossFundTabs,
    CashCallCoLeadFinancialSummary,
    LoadingPanel,
    CashCallUploadDocumentType,
    CashCallFormYoA,
    CashCallFormClaimDate,
    CashCallFormPolicyEffectiveDate,
    CashCallFormPolicyExpiryDate,
    CashCallFormReportPeriodDate,
    CashCallFormCountry,
    CashCallFormState,
    CashCallFormProvince,
    CashCallFormSactionsCheckStatus,
    CashCallDocumentUploader,
    CashCallFeeExists,
    CashCallFormClaimExists,
    CashCallFormBdxList,
    BaseInputCurrency,
    BaseInput,
    BaseButton,
    ErrorMessages
  },
  data() {
    return {
      claimReferences: [],
      claimBdxMapping: {},
      settingBdxFromClaim: false,
      //currentStep: 1,
      // totalSteps is a computed: co-lead adds a "Send" step (step 7).
      step1Attempted: false,
      activeAccordion: "fin-acc",
      // Single source of truth for document types, shared with the normal cash
      // call's upload UI (CashCallUploadDocumentType) so co-lead offers the exact
      // same list. "Other" is appended for the user-defined custom entry (renamed
      // via updateDocumentationOption). The co-lead PDF renders these same labels
      // (passed as document_types in buildColeadEmailPayload) so uploads match.
      documentationOptions: [
        ...CASH_CALL_SUPPORTING_DOCUMENT_OPTIONS.map((o) => o.value),
        "Other"
      ],
      selectedDocs: [],
      uploadedDocuments: {},
      // True only while initFormData() is loading a record into the form. The
      // is_co_lead watcher clears attached docs on a genuine user flow-switch,
      // but must NOT clear them during load (is_co_lead flips false->true as the
      // saved co-lead loads), or a reopened draft's saved files get wiped.
      isInitializing: false,
      // Message from the last failed saveForm() call, shown by the Compile flows.
      lastSaveError: '',
      uploadingDocuments: {},
      otherDescription: '',
      deletingFile: null,
      originalBroker: this.formData.broker,
      selectedBrokerId: 0,
      // LIQ-1006: RLS group ids for the manually chosen TPA / Coverholder.
      // Lincoln's create_new_key() builds the 7th/8th key segments from the
      // RLS_TPA / RLS_Coverholder primary keys, so the manual key can only be
      // composed correctly once these are known. Emitted by the pickers on
      // step 3 (@update:selectedTPAId / @update:selectedCoverholderId).
      manualTpaId: null,
      manualCoverholderId: null,
      // Every BDX key this user can see, cached so the manual path can look up
      // the TPA/Coverholder that belong to a UMR_RC_CC_SN the system already
      // knows. Fetched once by CashCallFormManualUMR (which needs the same
      // call for its suggestions) and handed up, rather than requested twice.
      manualBdxKeyPool: [],
      // Last (key, claim ref) pair looked up, so typing does not re-fetch.
      manualClaimSignature: '',
      // Fields written by the claim-details prefill, so a change of claim
      // reference can clear exactly those and leave typed values alone.
      manualPrefilledFields: [],
      manualPartyOptionsInFlight: null,
      // Set once the RLS lists have been asked for, so the prefill re-runs
      // itself exactly once rather than on every keystroke.
      manualPartyOptionsRequested: false,
      // Last key the underwriter/broker lookup ran for, so typing does not
      // re-request them on every keystroke.
      manualPartySignature: '',
      // Option lists published by the TPA / Coverholder pickers, so a prefill
      // can turn an id taken from the key into the NAME those pickers bind to.
      manualTpaOptions: [],
      manualCoverholderOptions: [],
      // True while a prefill is writing TPA/Coverholder, so the watchers that
      // clear the claim fields on a manual edit do not treat it as one.
      manualPartyPrefilling: false,
      isOtherDisabled: false,
      lossStateFromDb: false,
      lossSLStateFromDb: false
    }
  },
  emits: ['success', 'change'],
  props: {
    id: Number,
    status: String,
    value: Boolean,
    isModal: Boolean,
    claim_reason: String,
    claim_loss_established: Boolean,
    claim_authority_scope: Boolean,
    umr: String,
    bdx_key: String,
    claim_ref: String,
    cc: String,
    rc: String,
    sn: String,    
    currency_type: String,
    year_of_account: Number,
    tpa: String,
    broker: String,
    coverholder: String,
    insured_name: String,
    underwriter_lead: String,
    date_of_loss: String,
    date_claim_made: String,
    date_recent_report_period: String,
    date_risk_effective: String,
    date_risk_expiry: String,
    loss_country: String,
    loss_state: String,
    loss_sl_state: String,
    canada_province: String,
    sanctions_status: String,
    naic_code: String,

    fee_exists: Boolean,
    amount: Number,
    total_fee: Number,
    split_fee: Number,
    total_indemnity: Number,
    expenses: Number,
    attorney_fee: Number,
    adjusters_fee: Number,
    defence_fee: Number,
    tpa_fee: Number,
    upload_doc_type: String,
    displayType: String,
    initial_loss_fund_value: Number,
    current_loss_fund_balance: Number,
    supporting_documents: Array,
    // Cash Call Comments (Step 6). MUST be declared here - without the prop
    // declaration the parent's :cash_call_comments binding lands on $attrs, not
    // props, so initFormData()'s `props.cash_call_comments` reads undefined and
    // the field opens EMPTY when reopening a saved cash call (even though it IS
    // persisted - the management table's Cash Call Comments column shows it).
    cash_call_comments: String,
    // Statutory Deadline (Step 4). Declared so the parent's :statutory_deadline
    // binding reaches props (not $attrs) - otherwise initFormData reads
    // undefined and the field opens blank when reopening a saved cash call,
    // exactly like the cash_call_comments bug.
    statutory_deadline: String,
    is_co_lead: Boolean,
    // CashCallKeySerializer objects (every key on a Co-Lead cash call) -
    // mapped into formData.co_lead_keys's internal row shape in initFormData().
    keys: Array
  },
  methods: {
    /**
     * LIQ-1006: derives `bdx_key_manual` from the six manually entered BDX
     * fields and mirrors it into `formData.bdx_key`, which is what actually
     * gets persisted - the backend reads `data.get('bdx_key')[0]` into
     * CashCallKey.Bdx_Key, and never looks at `bdx_key_manual`.
     *
     * Every blank component defaults to '0' - that is exactly what Lincoln's
     * create_new_key() does for risk_code/cat_code/section_no/currency/YoA, and
     * an empty segment would leave a hole in the key that no BDX row matches.
     *
     * The key MUST be the 8-part form. Lincoln's BDX import builds every stored
     * key with create_new_key() (utils/helpers.py builds UMR_Risk_Cat_Section,
     * copied verbatim into GlobalUMR.umr_rc_cc_sn), which ALWAYS appends the
     * TPA and Coverholder segments - using the literal '0' when a party is
     * unknown - so a stored key always has 8 parts. Every endpoint this form
     * reads (get/bdxkeys, get/claims, global-umr-claims-bulk) serves that
     * 8-part key straight from the DB. A 6-part key would therefore never match
     * a real BDX row, and Lincoln's own extract_tpa_and_coverholder_from_key()
     * ignores anything that isn't exactly 8 parts.
     *
     * buildManualBdxKey() only appends the pair when at least one id is known,
     * so pass a placeholder id when neither has been chosen yet to keep the
     * shape stable - the segment still renders as '0', matching Lincoln.
     */
    /**
     * LIQ-1006: fill TPA and Coverholder from the key the user is typing.
     *
     * Once UMR / Risk Code / Cat Code / Section No identify a UMR_RC_CC_SN the
     * system already holds, the parties behind it are usually already known -
     * asking the user to pick them again invites exactly the mismatch this is
     * meant to avoid: a manual entry whose composed key looks right but names
     * the wrong company (in testing, "Sedgwick" resolved to SED-51, which is
     * actually Sedgwick Delegated Authority).
     *
     * Only prefills when the match is UNAMBIGUOUS. ~1% of UMR_RC_CC_SN groups
     * carry more than one TPA/Coverholder pair (same TPA, different
     * coverholder), and guessing between them would write a wrong party with
     * no sign anything happened - so those are left for the user to choose.
     *
     * Never overwrites a value the user has already set, and both fields stay
     * editable afterwards: this is a head start, not a lock.
     */
    prefillManualPartiesFromKey() {
      if (this.formData.bdx_key_exists !== false) return

      const part = (v) => {
        const s = v === null || v === undefined ? '' : String(v).trim()
        return s === '' ? '0' : s
      }
      const umr = (this.formData.umr || '').trim()
      if (!umr) return

      const prefix = [
        umr,
        part(this.formData.rc),
        part(this.formData.cc),
        part(this.formData.sn)
      ].join('_')

      // Distinct (tpaSegment, coverholderSegment) pairs behind this prefix.
      const pairs = new Set()
      this.manualBdxKeyPool.forEach((key) => {
        const p = String(key || '').split('_')
        if (p.length !== 8) return
        if (p.slice(0, 4).join('_') !== prefix) return
        pairs.add(`${p[6]}|${p[7]}`)
      })

      // No match, or more than one - nothing safe to fill.
      if (pairs.size !== 1) return

      const [tpaSegment, coverholderSegment] = [...pairs][0].split('|')
      const idOf = (segment) => {
        const dash = String(segment || '').lastIndexOf('-')
        return dash === -1 ? null : Number(segment.slice(dash + 1)) || null
      }

      const tpaId = idOf(tpaSegment)
      const coverholderId = idOf(coverholderSegment)
      if (!tpaId && !coverholderId) return

      // The pickers are NAME-driven: they bind a name and emit the id they
      // resolved from it. Only the id is known here, so the name is looked up
      // in the options they published and written back through the same
      // v-model they own - setting the id directly would leave the dropdown
      // reading "Select" while the key carried a party the user never saw.
      const nameForId = (opts, id) => {
        const match = (opts || []).find((opt) => opt.id === id)
        return match ? match.value : null
      }

      // The pickers publish their options, but they live on STEP 3 - while the
      // user is still filling the key on step 2 those components have not
      // mounted, so their lists are empty and nothing can be resolved. Fetch
      // the same RLS groups here rather than depend on when a later step
      // happens to render; this is why an earlier version only worked after
      // visiting step 3 first.
      if (!this.manualTpaOptions.length || !this.manualCoverholderOptions.length) {
        // Load them, then run this again - but only once. Re-entering
        // unconditionally would loop when the lists come back empty, and the
        // in-flight guard inside the loader would make the second pass a no-op
        // anyway, leaving every field blank.
        if (!this.manualPartyOptionsRequested) {
          this.manualPartyOptionsRequested = true
          this.loadManualPartyOptions().then(() => this.prefillManualPartiesFromKey())
        }
        return
      }

      this.manualPartyPrefilling = true

      if (tpaId && !this.formData.tpa) {
        const name = nameForId(this.manualTpaOptions, tpaId)
        if (name) this.formData.tpa = name
      }
      if (coverholderId && !this.formData.coverholder) {
        const name = nameForId(this.manualCoverholderOptions, coverholderId)
        if (name) this.formData.coverholder = name
      }

      this.$nextTick(() => { this.manualPartyPrefilling = false })
    },
    /**
     * LIQ-1006: fill the claim details when the manually composed key and claim
     * reference turn out to name a claim LimeIQ already holds.
     *
     * Usually they do not - the whole reason for this path is a claim raised
     * before its BDX arrived - so this is a convenience for the case where the
     * data happens to be there already, not something the path depends on. When
     * nothing matches, every field is left exactly as the user typed it.
     *
     * Reuses getClaimDetails, the same call the BDX-exists path makes, so the
     * two paths populate from one source.
     */
    async prefillManualClaimDetails() {
      if (this.formData.bdx_key_exists !== false) return

      const key = (this.formData.bdx_key_manual || '').trim()
      const claimRef = (this.formData.claim_ref_manual || '').trim()
      if (!key || !claimRef) return
      // A key still carrying placeholder segments is not yet a real key.
      if (key.split('_').length !== 8) return

      // Don't re-fetch the same pair on every keystroke.
      const signature = `${key}|${claimRef}`
      if (this.manualClaimSignature === signature) return

      // The pair CHANGED, so anything filled from the previous one is now about
      // somebody else's claim. Clear it before looking up the new pair: leaving
      // it would show the old insured, dates and cause of loss against a claim
      // reference they do not belong to, and save them that way.
      if (this.manualClaimSignature) this.clearPrefilledClaimFields()
      this.manualClaimSignature = signature

      try {
        const response = await CashCallService.getClaimDetails({
          claim_ref: claimRef,
          umr: (this.formData.umr || '').trim(),
          bdx_key: key,
          role: this.userCompanyRole,
          role_value: this.userCompanyRoleValue
        })

        const output = (response && response.data && response.data.data && response.data.data.data) || null
        if (!output || !output.Claim_Reference) return

        // Only fill what the user has not already entered: this arrives after
        // they have been typing, and overwriting their work would be worse than
        // not helping at all.
        const fill = (field, value) => {
          if (value && !this.formData[field]) {
            this.formData[field] = value
            // Remembered so a later change of claim reference clears exactly
            // what was filled from the previous one, and nothing else.
            this.manualPrefilledFields.push(field)
          }
        }

        fill('insured_name', output.Insured_Full_Name)
        fill('date_of_loss', output.Date_of_Loss_From)
        fill('date_claim_made', output.Date_Claim_Made)
        fill('date_recent_report_period', output.Reporting_Period_End_Date)
        fill('date_risk_effective', output.Risk_Inception_Date)
        fill('date_risk_expiry', output.Risk_Expiry_Date)
        fill('date_binder_inception', output.Binder_Contract_Inception)
        fill('date_binder_expiry', output.Binder_Contract_Expiry)
        fill('loss_state', output.Loss_State)
        fill('loss_sl_state', output.State_of_Filing)
        fill('naic_code', output.NAIC_Code)
        if (output.Cause_of_Loss && !this.formData.cause_of_loss) {
          this.formData.cause_of_loss = output.Loss_Description
            ? `${output.Cause_of_Loss} - ${output.Loss_Description}`
            : output.Cause_of_Loss
          this.manualPrefilledFields.push('cause_of_loss')
        }
      } catch (err) {
        // The claim simply is not there yet, which is the normal case here.
        console.error('Error fetching claim details for manual entry', err)
      }
    },
    /**
     * Loads the TPA / Coverholder RLS groups the prefill resolves names from.
     * The step-3 pickers publish the same lists once they mount, but the key is
     * composed on step 2, so waiting for them would make the prefill depend on
     * which steps the user had already visited.
     */
    async loadManualPartyOptions() {
      // Already in flight: wait for that call rather than firing a second one
      // or returning early, which would leave the caller with empty lists.
      if (this.manualPartyOptionsInFlight) return this.manualPartyOptionsInFlight
      try {
        const toOptions = (rows, field) => (rows || [])
          .map((row) => ({ id: row.id, value: row[field] }))
          .filter((opt) => opt.id && opt.value)

        this.manualPartyOptionsInFlight = Promise.all([
          LincolnRLSGroupsService.list('TPA'),
          LincolnRLSGroupsService.list('Coverholder')
        ])
        const [tpaResp, chResp] = await this.manualPartyOptionsInFlight

        if (!this.manualTpaOptions.length) {
          this.manualTpaOptions = toOptions(tpaResp.data.data, 'TPA')
        }
        if (!this.manualCoverholderOptions.length) {
          this.manualCoverholderOptions = toOptions(chResp.data.data, 'coverholder')
        }
      } catch (err) {
        // Without these the prefill simply does not happen, which leaves the
        // user picking the parties by hand - the behaviour before this existed.
        console.error('Error loading party options for prefill', err)
      } finally {
        this.manualPartyOptionsInFlight = null
      }
    },
    /**
     * LIQ-1006: fill Lead Underwriter and Broker from the composed key.
     *
     * Neither is carried in the key itself (which holds only the TPA and
     * Coverholder ids) nor returned by getClaimDetails - they live in the key's
     * RLS associations, which is why an earlier version left both blank while
     * every other field populated. These are the same two calls the BDX-exists
     * path makes once a key is selected, given the manually composed key.
     */
    async prefillManualUnderwriterAndBroker() {
      if (this.formData.bdx_key_exists !== false) return

      const key = (this.formData.bdx_key_manual || '').trim()
      if (!key || key.split('_').length !== 8) return
      if (this.manualPartySignature === key) return
      this.manualPartySignature = key

      // The endpoints take the whole key as `umr`, as the BDX-exists path sends
      // it - not the UMR segment alone.
      const data = { umr: key }

      if (!this.formData.underwriter_lead) {
        try {
          const response = await CashCallService.getUnderwriter(data)
          const names = ((response.data.data.data) || {}).Underwriter_Name
          if (Array.isArray(names) && names.length && !this.formData.underwriter_lead) {
            this.formData.underwriter_lead = names.join(',')
          }
        } catch (err) {
          console.error('Error fetching lead underwriter for manual entry', err)
        }
      }

      if (!this.formData.broker || !this.formData.broker.length) {
        try {
          const response = await CashCallService.getBroker(data)
          const names = ((response.data.data.data) || {}).Broker_Name
          // Broker is array-valued on this form, unlike the other three.
          if (Array.isArray(names) && names.length &&
              (!this.formData.broker || !this.formData.broker.length)) {
            this.formData.broker = names
            this.originalBroker = names
          }
        } catch (err) {
          console.error('Error fetching broker for manual entry', err)
        }
      }
    },
    /**
     * Clears the fields prefillManualClaimDetails populated, and only those.
     * Tracked explicitly rather than clearing every claim field, so a value the
     * user typed themselves is never discarded by a lookup they did not ask for.
     */
    clearPrefilledClaimFields() {
      (this.manualPrefilledFields || []).forEach((field) => {
        this.formData[field] = ''
      })
      this.manualPrefilledFields = []
    },
    recomputeManualBdxKey() {
      if (this.formData.bdx_key_exists !== false) return

      const orZero = (v) => {
        const s = v === null || v === undefined ? '' : String(v).trim()
        return s === '' ? '0' : s
      }

      const base = buildManualBdxKey({
        umr: (this.formData.umr || '').trim(),
        riskCode: orZero(this.formData.rc),
        catCode: orZero(this.formData.cc),
        sectionNo: orZero(this.formData.sn),
        currency: orZero(this.formData.currency_type),
        yearOfAccount: orZero(this.formData.year_of_account)
      })

      // Append the party pair here rather than through buildManualBdxKey's own
      // optional tpaCompany/coverholderCompany arguments: that helper omits the
      // pair entirely when NEITHER id is known, which would emit a 6-part key.
      // Lincoln always writes both segments ('0' when a party is unknown), so
      // the pair is appended unconditionally here. The helper is shared with the
      // Co-Lead "Add missing UMR" flow, so its own behaviour is left untouched.
      const rlsPrefix = (name) => (name || '').replace(/\s/g, '').slice(0, 3).toUpperCase()
      const tpaSegment = this.manualTpaId
        ? `${rlsPrefix(this.formData.tpa)}-${this.manualTpaId}`
        : '0'
      const coverholderSegment = this.manualCoverholderId
        ? `${rlsPrefix(this.formData.coverholder)}-${this.manualCoverholderId}`
        : '0'

      // Only compose a key once the user has actually entered something. With
      // every field blank, orZero() turns each component into '0' and `base`
      // becomes the truthy string "0_0_0_0_0_0" - so an untouched form would
      // save the meaningless key "_0_0_0_0_0_0_0". The UMR is the one component
      // with no sensible placeholder, so treat it as the signal that real data
      // has been entered; until then the key stays empty.
      const hasEntry = !!(this.formData.umr || '').trim()
      const key = hasEntry ? [base, tpaSegment, coverholderSegment].join('_') : ''

      this.formData.bdx_key_manual = key
      // Persisted field: keep it in the same [key] array shape the BDX-list
      // path produces, so saveForm/build_cash_call_key_fields need no special
      // case for manual keys.
      this.formData.bdx_key = key ? [key] : []
    },
    updateDocumentationOption() {
      const lastIndex = this.documentationOptions.length - 1;
      const oldValue = this.documentationOptions[lastIndex];
      const newValue = this.otherDescription.length > 0 ? this.otherDescription : 'Other';

      // Update selectedDocs to match the renamed entry
      const selectedIdx = this.selectedDocs.indexOf(oldValue);
      if (selectedIdx !== -1) {
        this.$set(this.selectedDocs, selectedIdx, newValue);
      }

      // Also sync uploadedDocuments if files were uploaded under the old name
      if (oldValue !== newValue && this.uploadedDocuments[oldValue]) {
        this.$set(this.uploadedDocuments, newValue, this.uploadedDocuments[oldValue]);
        this.$delete(this.uploadedDocuments, oldValue);
      }

      this.$set(this.documentationOptions, lastIndex, newValue);
    },
    onDocCheckboxChange(doc) {
      // After v-model updates selectedDocs, clean up any stale "Other" entries
      // The "Other" option can be renamed to a custom value (otherDescription),
      // leaving orphaned entries in selectedDocs under the old name
      this.$nextTick(() => {
        const lastOption = this.documentationOptions[this.documentationOptions.length - 1];
        const isOtherRelated = doc === lastOption || doc === 'Other' || doc === this.otherDescription;

        if (isOtherRelated) {
          // If the current checkbox value is NOT in selectedDocs, it was unchecked
          // Remove all possible "Other" variants from selectedDocs
          if (!this.selectedDocs.includes(doc)) {
            this.selectedDocs = this.selectedDocs.filter(
              (d) => d !== 'Other' && d !== this.otherDescription && d !== lastOption
            );
          }
        }

        // Clear validation errors when user deselects a doc type
        if (this.errors.length > 0) {
          this.errors = [];
        }
      });
    },
    ifActiveCollapse(id) {
      if (this.activeAccordion === id) {
        this.activeAccordion = null;
      }
    },
    onAccordionShow(id) {
      this.activeAccordion = id;
    },
    onAccordionHide(id) {
      if (this.activeAccordion === id) {
        this.activeAccordion = null;
      }
    },
    async validateCurrentStep() {
      let observer = null;

      if (this.currentStep === 1) observer = this.$refs.step1Observer;
      if (this.currentStep === 2) observer = this.$refs.step2Observer;
      if (this.currentStep === 3) observer = this.$refs.step3Observer;
      if (this.currentStep === 4) observer = this.$refs.step4Observer;
      if (this.currentStep === 5) observer = this.$refs.step5Observer;


      if (!observer) return;

      if (this.currentStep === 1) {
        this.step1Attempted = true
      }

      let isValid = await observer.validate();

      // Step 3 Co-Lead renders one UMR per tab, so the ValidationObserver above
      // only sees the ACTIVE tab's fields - a required field left blank on any
      // other tab would slip through. Additionally check every UMR's required
      // fields at the data level (this also switches to the first offending tab
      // and flags it) before allowing Save & Next.
      if (isValid && this.currentStep === 3 && this.formData.is_co_lead) {
        const claimDetails = this.$refs.coLeadClaimDetails;
        if (claimDetails && typeof claimDetails.validateAllRows === 'function') {
          isValid = claimDetails.validateAllRows();
        }
      }

      // Step 4: Indemnity must be a real amount. vee-validate's `required` rule
      // passes on 0, and the field pre-fills with 0 (so it is never "empty") -
      // which let a $0 cash call through to the next step. Require > 0 here.
      if (isValid && this.currentStep === 4) {
        const rawIndemnity = this.formData.total_indemnity
        const indemnity = Number(
          String(rawIndemnity === null || rawIndemnity === undefined ? '' : rawIndemnity)
            .replace(/,/g, '')
            .replace(/\$/g, '')
        )
        if (!indemnity || isNaN(indemnity) || indemnity <= 0) {
          isValid = false
          this.errors = [
            {
              label: 'Indemnity',
              message: 'Please enter an Indemnity amount greater than 0.'
            }
          ]
          Vue.prototype.$toasted.show('Please enter an Indemnity amount greater than 0.')
        }
      }

      if (isValid) {
        this.currentStep = Math.min(this.currentStep + 1, this.totalSteps)
        if (this.currentStep > this.maxVisitedStep) {
          this.maxVisitedStep = this.currentStep
          if (this.formData.id) {
            localStorage.setItem(`cashcall_step_${this.formData.id}`, this.maxVisitedStep);
          }
        }
      } else {
        // Scroll to top of form
        const container = this.$refs.formContainer;
        if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    nextStep() {
      if (this.currentStep < this.totalSteps) this.currentStep++;
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep--;
      this.errors = [];
    },
    jumpToStep(stepNumber) {
      this.currentStep = stepNumber;
      this.errors = [];

      // Smooth scroll to top so form fields appear correctly
      this.$nextTick(() => {
        if (this.$refs.formContainer) {
          this.$refs.formContainer.scrollTo({ top: 0, behavior: "smooth" });
        }
      });
    },
    async onHeaderClick(targetStep) {
      // Only allow in Edit mode or Review page
      if (!this.isHeaderClickable) {
        return;
      }

      // If clicking same step, ignore
      if (targetStep === this.currentStep) {
        return;
      }

      // Block forward jumps beyond the highest visited step
      if (targetStep > this.maxVisitedStep) {
        return;
      }

      // For backward navigation, jump freely without validation
      if (targetStep < this.currentStep) {
        this.currentStep = targetStep;
        return;
      }

      // For forward navigation (within visited range), validate current step first
      const currentObserver = this.$refs[`step${this.currentStep}Observer`];

      if (currentObserver) {
        const isValid = await currentObserver.validate();
        if (!isValid) {
          return; // stay on current step
        }
      }

      this.currentStep = targetStep;
    },
    closeModal() {
      this.$emit('input', false)
    },
    onClaimExistsChange(val) {
      console.log('onClaimExistsChange', val, 'claimref=', this.formData.claimref)

      this.formData.claim_exists = val

      // user switched to NO
      if (val === false) {
        //this.formData.claimref = []
        this.claimReferences = []
        return
      }

      // user switched back to YES
      if (val === true) {

        // if BDX already selected reload claims
        if (this.formData.bdx_key && this.formData.bdx_key.length > 0) {
          const bdxKey = this.formData.bdx_key[0]
          this.handleSelectedBdxKey(bdxKey)
        }
      }
      },
    async getDataRowsForSelectedClaim(claimRef = '', umr = '') {
      const data = {
        claim_ref: claimRef,
        umr: umr,
        bdx_key: this.formData.bdx_key[0] || '',
        role: this.userCompanyRole,
        role_value: this.userCompanyRoleValue
      }
      try {
        const response = await CashCallService.getClaimDetails(data)
        if (response.status == 200) {
          console.log(response.data.data.data)
          const output = response.data.data.data
          this.formData.insured_name = output.Insured_Full_Name
          this.formData.date_of_loss = output.Date_of_Loss_From
          this.formData.date_claim_made = output.Date_Claim_Made
          this.formData.date_recent_report_period = output.Reporting_Period_End_Date
          this.formData.date_risk_effective = output.Risk_Inception_Date
          this.formData.date_risk_expiry = output.Risk_Expiry_Date
          this.formData.date_binder_inception = output.Binder_Contract_Inception
          this.formData.date_binder_expiry = output.Binder_Contract_Expiry
          //this.formData.loss_country = output.Insured_Country
          this.formData.loss_state = output.Loss_State
          this.formData.loss_sl_state = output.State_of_Filing

          // mark whether it came from DB
          this.lossStateFromDb = !!output.Loss_State
          this.lossSLStateFromDb = !!output.State_of_Filing

          this.formData.tpa = output.TPA_Name
          //this.formData.coverholder = output.Coverholder_Name
          this.formData.cause_of_loss =
              output.Cause_of_Loss + ' - ' + output.Loss_Description
          this.formData.naic_code = output.NAIC_Code
          if(this.formData.bdx_key[0] != output.UMR_Risk_Cat_Section) {
            console.log('Updating the Bdx Key')
            this.settingBdxFromClaim = true
            this.formData.bdx_key = [output.UMR_Risk_Cat_Section]
            this.$nextTick(() => { this.settingBdxFromClaim = false })
            this.getParameters(this.formData.bdx_key[0])
          }
          else {
            this.getParameters('')
          }
        } else {
          this.resetClaimFields()
        }
      } catch (err) {
        console.error('Error fetching Claims', err)
        this.resetClaimFields()
        //this.loading = false
      }
    },
    async submitCashCallForm() {

      const observer = this.$refs.step6Observer
      const isValid = await observer.validate()

      if (!isValid) {
        return
      }

      this.loading = true
      try {
        const financialFields = [
          'amount',
          'total_fee',
          'total_indemnity',
          'expenses_amount',
          'attorney_coverage_amount',
          'adjuster_fee_amount',
          'defence_fees_amount',
          'tpa_fees_amount',
          'initial_loss_fund_value',
          'current_loss_fund_balance'
        ]

        const toNumberOrZero = (v) => {
          if (v === '' || v === null || v === undefined) return 0
          const n = parseFloat(String(v).replace(/,/g, '').replace(/\$/g, ''))
          return isNaN(n) ? 0 : n
        }

        // Collect invalid fields
        financialFields.forEach((field) => {
          this.formData[field] = toNumberOrZero(this.formData[field])
        })



        const data = this.formData ? { ...this.formData, user: this.userId, status: 'SAVED', new_broker_id: this.selectedBrokerId } : { user: this.userId };
        const jsonData = JSON.stringify(data)
        
        this.lastSaveError = ''
        const response  = await this.saveForm("SAVED", data);
        console.log('Create Cash Call API Response')
        console.log(response)
        // Nothing was saved (saveForm returns 0 on failure) - stay on the form
        // rather than report a compile that did not happen.
        if (!response) {
          Vue.prototype.$toasted.show(this.lastSaveError, { type: 'error' })
          return
        }
        this.hasChanges = false
        localStorage.removeItem(`cashcall_step_${this.formData.id}`);
        this.currentStep = 1;
        // Capture the attached doc types BEFORE clearing uploadedDocuments: the
        // create response doesn't carry supporting_documents, and a brand-new
        // cash call isn't in the list yet, so the emailed PDF had no way to know
        // what was attached and showed every row as "Not Attached". Pass them
        // along with the success event so the email path can use them.
        const attachedDocs = Object.keys(this.uploadedDocuments || {})
          .filter((type) => (this.uploadedDocuments[type] || []).length > 0)
          .map((type) => ({ document_type: type }));
        this.uploadedDocuments = {};  // clear all uploaded files
        this.$emit('success');
        Vue.prototype.$emitter.emit('cash-call-form:success', {
            status: true,
            obj: response,
            supporting_documents: attachedDocs
        })
        Vue.prototype.$toasted.show('Cash Call compiled successfully')
        this.resetFormFields();

        // If new broker -> Assign the RLS Broker to the global umr instance.

        if(this.selectedBrokerId != 0)
        {
          const payload = {
          bdx_key: this.formData.bdx_key[0],
          broker_id: this.selectedBrokerId
          }

          await CashCallService.setBroker(payload);
        }      
      

      } catch (err) {
        console.error('Error Creating Cash Call', err)
        //this.loading = false
      } finally {
        this.loading = false
      }
    },

    async getParameters(val) {
      //this.loading = true;
      const data = {
        umr: val == '' ? this.formData.bdx_key[0] : val
      }

      // Get RLS TPA
      try {
        const response = await CashCallService.getTPA(data)
        const parameters = response.data.data.data
        const tpa_array = parameters.TPA_Name
        this.formData.tpa = tpa_array.join(',')
      } catch (err) {
        console.error('Error fetching TPA', err)
      }

      // Get RLS Underwriters
      try {
        const response = await CashCallService.getUnderwriter(data)
        const parameters = response.data.data.data
        const underWriter_array = parameters.Underwriter_Name
        this.formData.underwriter_lead = underWriter_array.join(',')
      } catch (err) {
        console.error('Error fetching Lead Underwriters', err)
      }

      // Get RLS Brokers
      try {
        const response = await CashCallService.getBroker(data)
        const parameters = response.data.data.data
        const broker_array = parameters.Broker_Name
        //this.formData.broker = this.formData.broker || broker_array.join(',')
        this.formData.broker = this.formData.broker && this.formData.broker.length ? this.formData.broker : broker_array
        this.originalBroker =  this.formData.broker
      } catch (err) {
        console.error('Error fetching Brokers', err)
      }

      // Get RLS Coverholders
      try {
        const response = await CashCallService.getCoverholder(data)
        const parameters = response.data.data.data
        const coverholder_array = parameters.Coverholder_Name
        this.formData.coverholder = coverholder_array.join(',')
      } catch (err) {
        console.error('Error fetching Coverholders', err)
      }
    },
    resetClaimFields() {
      this.formData.insured_name = ''
      this.formData.date_of_loss = ''
      this.formData.date_claim_made = ''
      this.formData.date_recent_report_period = ''
      this.formData.date_risk_effective = ''
      this.formData.date_risk_expiry = ''
      this.formData.date_binder_inception = ''
      this.formData.date_binder_expiry = ''
      this.formData.loss_country = 'USA'
      this.formData.loss_state = ''
      this.formData.loss_sl_state = ''
      this.formData.canada_province = ''
      this.formData.tpa = ''
      this.formData.coverholder = ''
      this.formData.cause_of_loss = ''
      this.formData.naic_code = ''
      this.lossStateFromDb = false
      this.lossSLStateFromDb = false
    },
    resetFormFields() {
      this.formData.id = null
      this.formData.claim_loss_established = true
      this.formData.claim_reason = ''
      this.formData.reason_manual = ''
      this.formData.claim_authority_scope = undefined
      this.formData.bdx_key_exists = true

      //umr: [],

      //BDX Key Fields
      this.formData.umr = ''
      this.formData.sn = ''
      this.formData.rc = ''
      this.formData.cc = ''
      this.formData.currency_type = ''
      this.formData.year_of_account = 0
      this.formData.bdx_key = []
      this.formData.bdx_key_manual = ''
      this.formData.claimref = []
      this.formData.claim_ref_manual = ''

      //RLS Fields
      this.formData.tpa = ''
      this.formData.broker = ''
      this.formData.coverholder = ''
      this.formData.underwriter_lead = ''

      // Claim Fields
      this.formData.insured_name = ''
      this.formData.date_of_loss = ''
      this.formData.date_claim_made = ''
      this.formData.date_risk_effective = ''
      this.formData.date_risk_expiry = ''
      this.formData.date_binder_inception = ''
      this.formData.date_binder_expiry = ''
      this.formData.loss_country = 'USA'
      this.formData.loss_state = ''
      this.formData.loss_sl_state = ''
      this.formData.canada_province = ''
      this.formData.sanction_status = ''
      this.formData.naic_code = ''
      this.formData.cause_of_loss = ''

      // Finance Fields
      this.formData.amount = ''
      this.formData.total_fee = ''
      this.formData.split_fee = ''
      this.formData.total_indemnity = ''
      this.formData.expenses_amount = ''
      this.formData.attorney_coverage_amount = ''
      this.formData.adjuster_fee_amount = ''
      this.formData.defence_fees_amount = ''
      this.formData.tpa_fees_amount = ''
      this.formData.initial_loss_fund_value = ''
      this.formData.current_loss_fund_balance = ''

      this.formData.sl_tax = []
      this.formData.date_of_request = ''
      this.formData.account_id = ''
      this.formData.upload_doc_type = ''

      this.formData.account_name = ''
      this.formData.account_code = ''
      this.formData.account_type = ''
      this.selectedBrokerId = 0
      this.formData.new_broker_id = 0

      this.formData.cash_call_comments = ''

      // Reset the documentation options
      this.$set(this.documentationOptions, this.documentationOptions.length - 1, 'Other');
    },
    async handleClaimReferences(claims) {
      console.log('Recieved the initial claim references')
      console.log(claims)
      this.claimBdxMapping = {}
      claims.forEach(item => {
        this.claimBdxMapping[item.claim_reference] = item.bdx_key
      })
      this.claimReferences = claims.map(item => ({
        text: item.claim_reference,
        value: item.claim_reference
      }))
    },
    async handleSelectedBdxKey() {

      // When BDX was set programmatically by a claim selection, don't filter claims by BDX
      if (this.settingBdxFromClaim) return

      console.time("handleSelectedBdxKey start");
      console.log('handleSelectedBdxKey start', this.formData.claimref)

      const selectedBdxKey =
        Array.isArray(this.formData.bdx_key) && this.formData.bdx_key.length > 0
          ? this.formData.bdx_key[0]
          : null;

          console.log("Bdx Key Emitted :", selectedBdxKey);

          if (!selectedBdxKey) {
            // TPA users have pre-populated claims — restore them when BDX is deselected
            if (Object.keys(this.claimBdxMapping).length > 0) {
              this.claimReferences = Object.keys(this.claimBdxMapping).map(claimRef => ({
                text: claimRef,
                value: claimRef
              }))
            } else {
              this.claimReferences = [];
            }
            return;
          }

          const data = {
            bdx_key: selectedBdxKey,
            role: this.userCompanyRole,
            role_value: this.userCompanyRoleValue
          };

          try {

            // Key components are always at the front: UMR_RC_CC_SN_Currency_Year.
            // Newer keys append _<TPA-id>_<Coverholder-id> at the end; we ignore
            // those trailing parts here.
            const dd = selectedBdxKey.split("_");

            this.formData.umr = dd[0] || "";
            this.formData.rc = dd[1] || "";
            this.formData.cc = dd[2] || "";
            this.formData.sn = dd[3] || "";
            this.formData.currency_type = dd[4] || "";
            this.formData.year_of_account = dd[5] ? Number(dd[5]) : 0;

            // reset claim dropdown properly
            // if (!this.formData.id) {
            //   this.formData.claimref = [];
            // }            
            this.claimReferences = [];

            const response = await CashCallService.getClaims(data);

            if (response.status === 200) {

              const output = response.data.data.data;

              const claims = output && output.claim_reference || [];

              this.claimReferences =
                claims.length === 0
                  ? []
                  : [...new Set(claims)].map((key) => ({
                      text: key,
                      value: key
                    }));
            }

          } catch (err) {
            console.error("Error fetching Claims", err);
            this.claimReferences = [];
          }
          console.timeEnd("handleSelectedBdxKey start");
  },
    onClaimRefChange(newVal) {
      console.log('Selected Claim is: ' + newVal)
      if (!newVal || !newVal.length || !newVal[0]) return
      const claimRef = newVal[0]
      const bdxKey = this.claimBdxMapping[claimRef]
      if (bdxKey) {
        this.settingBdxFromClaim = true
        this.formData.bdx_key = [bdxKey]
        const dd = bdxKey.split('_')
        this.formData.umr = dd[0] || ''
        this.formData.rc = dd[1] || ''
        this.formData.cc = dd[2] || ''
        this.formData.sn = dd[3] || ''
        this.formData.currency_type = dd[4] || ''
        this.formData.year_of_account = dd[5] ? Number(dd[5]) : 0
        this.$nextTick(() => { this.settingBdxFromClaim = false })
      }
      this.getDataRowsForSelectedClaim(claimRef, this.formData.umr)
    },
    triggerUpload(docType) {
      const refName = "file_" + docType;
      const input = this.$refs[refName];

      if (Array.isArray(input)) {
        input[0].click();
      } else if (input) {
        input.click();
      }
    },

    async handleFileUpload(event, docType, cash_call_id='') {
      cash_call_id = this.formData.id !== null && this.formData.id > 0 ? this.formData.id : cash_call_id;
      const files = Array.from(event.target.files);

      // Initialize uploadedDocuments array if not present
      if (!this.uploadedDocuments[docType]) {
        this.$set(this.uploadedDocuments, docType, []);
      }

      for (const file of files) {
        this.$set(this.uploadingDocuments, docType, true);
        // Check if file is already uploaded
        const alreadyUploaded = this.uploadedDocuments[docType].some(
            (f) => f.file.name === file.name && f.doc_type === docType
        );
        if (alreadyUploaded) {
          continue;
        }

        try {

          const formData = new FormData();
          formData.append("file", file);
          formData.append("doc_type", docType);

          // Upload the file
          const { data } = await CashCallService.uploadFile(cash_call_id, formData);
          const { data: results } = data || {}

          if (results.status === 200) {
            // On success, add to uploadedDocuments
            this.uploadedDocuments[docType].push({"file": file, "ETAG": results.data.ETAG, "cash_call_id": cash_call_id, "doc_type": docType});

            // Check if the current docType is the last item (i.e., "Other" or user-defined value)
            const isOtherSection = this.documentationOptions[this.documentationOptions.length - 1] === docType || this.documentationOptions[this.documentationOptions.length - 1] === this.otherDescription;

            // If the current section is "Other", disable the input field
            if (isOtherSection) {
              this.isOtherDisabled = true;
            }

            // Optional toast notification
            if (this.$bvToast && this.$bvToast.toast) {
              this.$bvToast.toast(`Uploaded ${file.name} for ${docType}`, {
                title: "Upload Complete",
                variant: "success",
                solid: true,
              });
            }
          } else {
            console.log(results.error)
            // Optional: show error notification
            if (this.$bvToast && this.$bvToast.toast) {
              this.$bvToast.toast(`Failed to upload ${file.name}`, {
                title: "Upload Error",
                variant: "danger",
                solid: true,
              });
            }
          }
        } catch (err) {
          console.error("Upload failed for file", file.name, err);

          // Optional: show error notification
          if (this.$bvToast && this.$bvToast.toast) {
            this.$bvToast.toast(`Failed to upload ${file.name}`, {
              title: "Upload Error",
              variant: "danger",
              solid: true,
            });
          }
        } finally {
          // Clear the input so same file can be re-selected if needed
          event.target.value = null;

          // Reset the uploading state to false after the upload is complete
          this.$set(this.uploadingDocuments, docType, false);
        }
      }
    },

    async deleteUploadedFile(docType, index) {
      const arr = this.uploadedDocuments[docType];
      if (!arr) return;

      const file = arr[index].file;
      this.deletingFile = docType + "_" + file.name;

      try {
        const {data} = await CashCallService.deleteFile(arr[index].cash_call_id, arr[index].ETAG, arr[index].doc_type);
        const {data: results} = data || {}
        if (results.status === 200) {
          arr.splice(index, 1);
          this.$set(this.uploadedDocuments, docType, [...arr]);

          // Check if "Other" section is empty and re-enable input if so
          const isOtherSection = this.documentationOptions[this.documentationOptions.length - 1] === docType || this.documentationOptions[this.documentationOptions.length - 1] === this.otherDescription;
          if (isOtherSection && this.uploadedDocuments[docType].length === 0) {
            this.isOtherDisabled = false;
          }

          // Optional toast
          if (this.$bvToast && this.$bvToast.toast) {
            this.$bvToast.toast(`Removed file from ${docType}`, {
              title: "File Deleted",
              variant: "success",
              solid: true,
            });
          }
        }
      }
      catch (err) {
        console.error("Removing failed for file", file.name, err);
        if (this.$bvToast && this.$bvToast.toast) {
          this.$bvToast.toast(`Failed to remove ${file.name}`, {
            title: "Upload Error",
            variant: "danger",
            solid: true,
          });
        }
      } finally {
        // Reset the deletingFile state once the process is complete
        this.deletingFile = null;
      }
    },

    initializeUploadedDocumentsFromProps() {
      console.log('inside initializeUploadedDocumentsFromProps');
      console.log(this.supporting_documents);
      if (!this.supporting_documents || !Array.isArray(this.supporting_documents)) {
        console.log('Not an Array')
        return;
      }

      this.uploadedDocuments = {}; // reset

      this.supporting_documents.forEach(doc => {
        const docType = doc.document_type;

        if (!this.uploadedDocuments[docType]) {
          this.$set(this.uploadedDocuments, docType, []);
        }

        this.uploadedDocuments[docType].push({
          file: {
            name: doc.filename,
            path: doc.path,      // optional (useful for download)
            ETAG: doc.ETAG,      // optional
            existing: true       // 🔥 important flag
          },
          ETAG: doc.ETAG,
          cash_call_id: doc.cash_call,
          doc_type: docType,
          existing: true         // mark as already saved in backend
        });
        if (!this.selectedDocs.includes(docType)) {
          this.selectedDocs.push(docType);
        }
      });
      
    
      console.log('uploaded documents')
      console.log(this.uploadedDocuments)
      console.log('selected documents')
      console.log(this.selectedDocs)
    },

    coLeadFormatMoney(value) {
      const number = Number(value) || 0
      return number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    // Co-Lead "Compile Cash Call" (step 6, final step). Mirrors the single-key
    // Compile flow: validate the review step, persist the cash call as SAVED,
    // close the wizard, and raise the "Email Cash Call Now?" prompt. The prompt
    // + recipient modal live in this component (kept mounted while the wizard is
    // hidden) so the co-lead PDF/send path stays intact.
    async onColeadCompile() {
      if (this.savingDraft) return
      this.savingDraft = true
      try {
        await this.$nextTick()
        const observer = this.$refs.step6Observer
        if (observer) {
          const isValid = await observer.validate()
          if (!isValid) return
        }

        // Same guard as the normal cash call: a selected document type with no
        // uploaded file must block compile (the user ticked "Engineer / Expert
        // Report" but attached nothing). Show which types are missing files.
        const docsWithoutFiles = this.selectedDocs.filter((doc) => {
          return !this.uploadedDocuments[doc] || this.uploadedDocuments[doc].length === 0
        })
        if (docsWithoutFiles.length > 0) {
          this.errors = [
            {
              label: 'Supporting Documents',
              message:
                'You have selected the following document type(s) but have not uploaded any files: ' +
                docsWithoutFiles.join(', ') +
                '. Please upload the required documents or deselect them on the Documentation page.'
            }
          ]
          this.$nextTick(() => {
            const container = this.$refs.formContainer
            if (container) {
              container.scrollTo({ top: 0, behavior: 'smooth' })
            }
          })
          return
        }
        this.errors = []

        this.lastSaveError = ''
        const saved = await this.saveForm('SAVED', null)
        // Nothing was saved (saveForm returns 0 on failure) - stay on the form
        // rather than report a compile that did not happen.
        if (!saved) {
          Vue.prototype.$toasted.show(this.lastSaveError, { type: 'error' })
          return
        }
        const id = Number((saved && saved.id) || this.formData.id || 0)
        if (id > 0) {
          this.formData.id = id
        }
        // Capture the server-generated reference (e.g. BRIT-0123-260623-CL) so
        // the co-lead PDF header shows it. Backend uses its own stored value
        // for the email subject/token regardless.
        const savedRef = saved && (saved.cash_call_reference || saved.reference)
        if (savedRef) {
          this.$set(this.formData, 'cash_call_reference', savedRef)
        }

        // Refresh the parent list now that it's compiled.
        this.hasChanges = false
        this.$emit('change')
        Vue.prototype.$toasted.show('Cash Call - Co-Lead compiled successfully')

        // Hand the whole email flow to the PARENT (CashCallDetails), which stays
        // mounted. Nesting the prompt inside this wizard modal breaks: closing
        // the wizard tears the nested modal down with it. So we build a
        // self-contained payload (everything the co-lead PDF/send needs) and let
        // the parent own the "Email Co-Lead Cash Call Now?" prompt + recipient
        // modal + send. Then close the wizard.
        const payload = this.buildColeadEmailPayload()
        Vue.prototype.$emitter.emit('cashcall:colead-compiled', payload)
        this.$emit('success')
      } finally {
        this.savingDraft = false
      }
    },

    // Snapshot of everything the parent needs to render the prompt and build /
    // send the combined co-lead PDF, independent of this wizard's lifecycle.
    buildColeadEmailPayload() {
      // Currency inputs hold display strings ("$100", "$1,000"); strip the mask
      // to a real number so the PDF doesn't get NaN -> 0.
      const parseMoney = (v) => {
        if (v === null || v === undefined || v === '') return 0
        const n = Number(String(v).replace(/[^0-9.-]/g, ''))
        return isNaN(n) ? 0 : n
      }
      const total = parseMoney(this.formData.amount)
      const rows = (this.formData.co_lead_keys || []).map((k) => {
        const sharePercentage = Number(k.share_percentage) || 0
        return {
          amount: Math.round(total * sharePercentage) / 100,
          funded: k.funding_required !== false
        }
      })
      const round2 = (n) => Math.round(n * 100) / 100
      return {
        cashCallId: this.formData.id,
        reference: this.formData.cash_call_reference || this.formData.reference || 'cash-call',
        recipientEmails: this.formData.recipient_emails || '',
        currencyPrefix: '$',
        keyCount: rows.length,
        totalAmount: round2(rows.reduce((s, r) => s + r.amount, 0)),
        totalRequired: round2(rows.filter((r) => r.funded).reduce((s, r) => s + r.amount, 0)),
        // The fee / loss-fund inputs store DISPLAY strings like "$100" or
        // "$1,000" (a currency mask), so Number("$100") = NaN -> 0 in the PDF.
        // Parse them by stripping everything but digits, minus and dot. Applied
        // to the pdfContext fees AND each key's loss-fund values below.
        keys: (this.formData.co_lead_keys || []).map((row) => ({
          ...row,
          initial_loss_fund_value: parseMoney(row.initial_loss_fund_value),
          current_loss_fund_balance: parseMoney(row.current_loss_fund_balance)
        })),
        pdfContext: {
          reference: this.formData.cash_call_reference || this.formData.reference || '',
          amount: parseMoney(this.formData.amount),
          comments: this.formData.cash_call_comments || '',
          claim_loss_established: this.formData.claim_loss_established,
          claim_reason: this.formData.claim_reason === 'Other'
            ? this.formData.reason_manual
            : this.formData.claim_reason,
          claim_authority_scope: this.formData.claim_authority_scope,
          indemnity: parseMoney(this.formData.total_indemnity),
          tpa_fees: parseMoney(this.formData.tpa_fees_amount),
          adjusters_fees: parseMoney(this.formData.adjuster_fee_amount),
          defence_fees: parseMoney(this.formData.defence_fees_amount),
          expenses: parseMoney(this.formData.expenses_amount),
          attorney_coverage: parseMoney(this.formData.attorney_coverage_amount),
          total_indemnity: parseMoney(this.formData.total_indemnity),
          total_fee: parseMoney(this.formData.total_fee),
          // Build the attached-documents list from what the user ACTUALLY
          // attached in this modal (uploadedDocuments, keyed by doc type), not
          // the `supporting_documents` prop (which reflects a previously-saved
          // cash call and was empty for a fresh co-lead - so the PDF showed
          // every document as "Not Attached"). Any doc type with >= 1 file
          // counts as attached.
          supporting_documents: Object.keys(this.uploadedDocuments || {})
            .filter((type) => (this.uploadedDocuments[type] || []).length > 0)
            .map((type) => ({ document_type: type })),
          // The exact doc-type labels this modal offers, so the PDF renders the
          // SAME rows the user uploaded against (Adjuster Report, Sanction Check,
          // ...) instead of a legacy hardcoded list whose labels never matched
          // and so always showed "Not Attached".
          document_types: [...this.documentationOptions]
        },
        requestor: {
          name: (this.currentUser && (this.currentUser.username || this.currentUser.name)) || '',
          company: 'LimeIQ',
          email: (this.currentUser && this.currentUser.email) || ''
        }
      }
    },

    async onSaveAndNext() {
      if (this.savingDraft) return;

      this.savingDraft = true;
      console.log('saving draft cash call')

      await this.$nextTick();

      try {
        //const observer = this.$refs.step2Observer;
        const observer = this.$refs[`step${this.currentStep}Observer`];
        if (!observer) return;

        const isValid = await observer.validate();
        if (!isValid) return;

        // Step 3 Co-Lead puts each UMR on its own tab, so the observer above
        // only validated the ACTIVE tab. Also enforce every UMR's required
        // fields at the data level (switches to and flags the first offending
        // tab) before saving/advancing.
        if (this.currentStep === 3 && this.formData.is_co_lead) {
          const claimDetails = this.$refs.coLeadClaimDetails;
          if (claimDetails && typeof claimDetails.validateAllRows === 'function' && !claimDetails.validateAllRows()) {
            const container = this.$refs.formContainer;
            if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
        }

        const statusToSave =  this.formData.id === 0 ? "DRAFT" : this.formData.status;

        const cashCallObj = await this.saveForm(statusToSave, null);

        const id = Number((cashCallObj && cashCallObj.id) || 0);

        if (id > 0) {
          this.formData.id = id;
          this.currentStep++;
          if (this.currentStep > this.maxVisitedStep) {
            this.maxVisitedStep = this.currentStep;
            localStorage.setItem(`cashcall_step_${id}`, this.maxVisitedStep);
          }
          // Land the newly-shown step at the top (e.g. Step 3 -> Step 4): the
          // user often clicks Next from the bottom of a long step, and without
          // this the next step opens scrolled halfway down. Wait for the new
          // step to render first.
          this.$nextTick(() => {
            const container = this.$refs.formContainer;
            if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
          });
        }
      } finally {
        this.savingDraft = false;
      }
    },


    async saveForm(status, payload) {
      try {
        const financialFields = [
          'amount',
          'total_fee',
          'total_indemnity',
          'expenses_amount',
          'attorney_coverage_amount',
          'adjuster_fee_amount',
          'defence_fees_amount',
          'tpa_fees_amount',
          'initial_loss_fund_value',
          'current_loss_fund_balance'
        ]

        const toNumberOrZero = (v) => {
          if (v === '' || v === null || v === undefined) return 0
          const n = parseFloat(String(v).replace(/,/g, '').replace(/\$/g, ''))
          return isNaN(n) ? 0 : n
        }

        // Build data first (draft or final)
        const data = payload == null
          ? { ...this.formData, status, user: this.userId, new_broker_id: this.selectedBrokerId }
          : { ...payload }

          // Normalize date fields (empty string -> null)
          const dateFields = [
            'date_of_loss',
            'date_claim_made',
            'date_risk_effective',
            'date_risk_expiry',
            'date_binder_inception',
            'date_binder_expiry',
            'date_recent_report_period',
            'date_of_request',
            'statutory_deadline'
          ]

          dateFields.forEach((field) => {
            if (data[field] === '' || data[field] === undefined) {
              data[field] = null
            }
          })

        // Normalize finance fields in the outgoing payload
        financialFields.forEach((field) => {
          data[field] = toNumberOrZero(data[field])
        })

        console.log('data is')
        console.log(data)
        console.log(payload == null ? 'Submitting Draft Version' : 'Submitting Final Version')

        if (data.is_co_lead) {
          const keys = (this.formData.co_lead_keys || []).map((key) => ({
            ...key,
            initial_loss_fund_value: toNumberOrZero(key.initial_loss_fund_value),
            current_loss_fund_balance: toNumberOrZero(key.current_loss_fund_balance)
          }))

          const groupResponse = await CashCallService.createCashCallGroup({
            ...data,
            cash_call_id: this.formData.id || null,
            keys
          })

          // The endpoint response is wrapped twice: axios `.data`, then the
          // service envelope { status, message, cash_call_id, data }, whose
          // inner `data` is the actual saved CashCall (with id + keys). Unwrap
          // to that object once.
          const saved = (groupResponse.data && groupResponse.data.data && groupResponse.data.data.data) || {}

          // Attach the persisted ids back onto the local rows so the NEXT save
          // updates these keys in place instead of creating duplicates. Match
          // by Bdx_Key (stable + unique per key) rather than array index, which
          // isn't guaranteed to line up with the server's returned order.
          const persistedKeys = saved.keys || []
          const idByBdxKey = {}
          persistedKeys.forEach((pk) => {
            const bk = pk && (pk.Bdx_Key || (Array.isArray(pk.bdx_key) ? pk.bdx_key[0] : pk.bdx_key))
            if (bk) idByBdxKey[bk] = pk.id
          })
          this.formData.co_lead_keys = this.formData.co_lead_keys.map((key, index) => {
            const bk = Array.isArray(key.bdx_key) ? key.bdx_key[0] : key.bdx_key
            const matchedId = idByBdxKey[bk] || (persistedKeys[index] && persistedKeys[index].id)
            return { ...key, id: matchedId || key.id }
          })

          return saved
        }

        const response = await CashCallService.createCashCall(data)
        console.log(response.data.data)
        return response.data.data
      }
      catch (err) {
        console.error("Error saving form", err)
        // Kept so the Compile flows can tell the user WHY nothing was saved
        // (e.g. LIQ-1198: the LimeIQ Key could not be reactivated in Lincoln).
        const body = err && err.response && err.response.data
        this.lastSaveError = (body && ((body.data && body.data.error) || body.detail || body.message)) ||
          'Cash Call could not be saved. Please try again.'
        return 0
      }
    },

    onFinalSubmit() {
      this.hasChanges = false

      // List of Financial Detail fields to validate
      const financialFields = [
        'amount',
        'total_fee',
        //'split_fee',
        'total_indemnity',
        'expenses_amount',
        'attorney_coverage_amount',
        'adjuster_fee_amount',
        'defence_fees_amount',
        'tpa_fees_amount'
      ]

      // Collect invalid fields
      const invalidFields = financialFields.filter((field) => {

        let raw = this.formData[field]

        // Treat blank finance fields as zero (valid case)
        if (raw === '' || raw === null || raw === undefined) {
          this.formData[field] = 0
          return false
        }

        let value = String(raw).replace(/,/g, '').replace(/\$/g, '')

        return isNaN(Number(value))
      })
      console.log('Errors Count: ' + invalidFields.length)

      if (invalidFields.length > 0) {
        // Show a clear error message
        this.errors = [
          {
            label: 'Finance Fields',
            message:
                'Please enter valid numeric values for all Financial Details fields.'
          }
        ]
        this.$nextTick(() => {
          const form = this.$refs.cashCallForm
          const container = this.$refs.formContainer
          if (container) {
            container.scrollTo({ top: 0, behavior: 'smooth' })
          }
          if (form) {
            form.scrollTo({ top: 0, behavior: 'smooth' })
          }
        })

        return
      }

      // Validate supporting documents: if a doc type is selected, at least one file must be uploaded
      const docsWithoutFiles = this.selectedDocs.filter((doc) => {
        return !this.uploadedDocuments[doc] || this.uploadedDocuments[doc].length === 0
      })

      if (docsWithoutFiles.length > 0) {
        this.errors = [
          {
            label: 'Supporting Documents',
            message:
              'You have selected the following document type(s) but have not uploaded any files: ' +
              docsWithoutFiles.join(', ') +
              '. Please upload the required documents or deselect them on the Documentation page.'
          }
        ]
        this.$nextTick(() => {
          const container = this.$refs.formContainer
          if (container) {
            container.scrollTo({ top: 0, behavior: 'smooth' })
          }
        })
        return
      }

      // Proceed if validation passes
      this.errors = []
      if (this.$toast && this.$toast.success) {
        this.$toast.success('Form validated successfully!')
      }

      console.log('Form submitted:', this.formData)
      this.submitCashCallForm()
    }
  },
  computed: {
    // LIQ-1006: the inputs the manual BDX key is composed from. Grouped into
    // one computed so a single deep watcher re-derives the key whenever any of
    // them changes, instead of six near-identical watchers. TPA/Coverholder are
    // included because they form the optional 7th/8th key parts - they're
    // chosen on step 3, so the key shown on step 2 is the 6-part form and
    // gains the party suffix once those are picked.
    // LIQ-1006: true when this cash call is being raised for a claim that does
    // not exist in LimeIQ yet - either because no BDX has been submitted for
    // the binder section at all, or because a BDX exists but this claim hasn't
    // appeared on it. In both cases nothing can auto-populate the claim detail
    // fields, so they must be editable rather than greyed out (LIQ-1754).
    isManualClaimEntry() {
      return this.formData.bdx_key_exists === false || this.formData.claim_exists === false
    },
    // A TPA-role user's own company is stamped on the cash call server-side
    // (CreateCashCallAPIView auto-populates `tpa` from their company's
    // role_value), so they must not be offered a TPA picker even on the
    // manual path. Superusers/admins and other roles do choose one.
    isTpaRoleUser() {
      return String(this.userCompanyRole || '').toUpperCase() === 'TPA'
    },
    // The RLS dropdowns (TPA / Underwriter / Coverholder) are array-valued -
    // they read props.value[0] and emit an array - but these three are stored
    // and sent as plain strings (build_cash_call_key_fields reads data['tpa'],
    // not data['tpa'][0]). Bridge between the two shapes here so the manual
    // path reuses the existing pickers without changing what gets persisted.
    manualTpa: {
      get() { return this.formData.tpa ? [this.formData.tpa] : [] },
      set(val) { this.formData.tpa = (Array.isArray(val) ? val[0] : val) || '' }
    },
    manualUnderwriter: {
      get() { return this.formData.underwriter_lead ? [this.formData.underwriter_lead] : [] },
      set(val) { this.formData.underwriter_lead = (Array.isArray(val) ? val[0] : val) || '' }
    },
    manualCoverholder: {
      get() { return this.formData.coverholder ? [this.formData.coverholder] : [] },
      set(val) { this.formData.coverholder = (Array.isArray(val) ? val[0] : val) || '' }
    },
    /**
     * LIQ-1006: what the "LimeIQ BDX Key" box SHOWS while the key is being
     * filled in. The stored key uses Lincoln's '0' for every unset component
     * (see recomputeManualBdxKey), which renders as a meaningless run of
     * "0_0_0_0" and gives the user no clue which field is still empty. This
     * substitutes the field's own name for anything not yet entered, so the
     * preview reads as a template that fills itself in as they type:
     *
     *   UMR_RC_CC_SN_CUR_YOA_TPA_CH   ->   B1234_23_11_22_USD_2024_HAU-42_ACM-7
     *
     * Display only - formData.bdx_key (what is persisted and what Lincoln is
     * matched against) is untouched and still carries the real '0's.
     */
    manualBdxKeyDisplay() {
      if (this.formData.bdx_key_exists !== false) return ''

      const orLabel = (value, label) => {
        const text = value === null || value === undefined ? '' : String(value).trim()
        return text === '' ? label : text
      }

      const rlsPrefix = (name) => (name || '').replace(/\s/g, '').slice(0, 3).toUpperCase()

      return [
        orLabel(this.formData.umr, 'UMR'),
        orLabel(this.formData.rc, 'RC'),
        orLabel(this.formData.cc, 'CC'),
        orLabel(this.formData.sn, 'SN'),
        orLabel(this.formData.currency_type, 'CUR'),
        orLabel(this.formData.year_of_account, 'YOA'),
        this.manualTpaId ? `${rlsPrefix(this.formData.tpa)}-${this.manualTpaId}` : 'TPA',
        this.manualCoverholderId ? `${rlsPrefix(this.formData.coverholder)}-${this.manualCoverholderId}` : 'CH'
      ].join('_')
    },
    manualBdxKeySource() {
      return {
        umr: this.formData.umr,
        rc: this.formData.rc,
        cc: this.formData.cc,
        sn: this.formData.sn,
        currency_type: this.formData.currency_type,
        year_of_account: this.formData.year_of_account,
        tpa: this.formData.tpa,
        coverholder: this.formData.coverholder,
        tpaId: this.manualTpaId,
        coverholderId: this.manualCoverholderId
      }
    },
    progressPercent() {
      return (this.currentStep - 1) / (this.totalSteps - 1) * 100;
    },
    showBrokerDropdown() {
      return !this.originalBroker // if broker not set => show dropdown
    },
    isEditMode() {
    return this.formData.id !== 0;
    },
    isReviewPage() {
      return this.currentStep === this.totalSteps;
    },    
    isHeaderClickable() {
      return this.isEditMode || this.isReviewPage;
    },
    coLeadShareTotal() {
      return totalSharePercentage(this.formData.co_lead_keys);
    },
    coLeadKeys() {
      return Array.isArray(this.formData.co_lead_keys) ? this.formData.co_lead_keys : [];
    },
    // Claim Details are shared across the whole co-lead (one claim), so the
    // Review step shows a SINGLE set sourced from the first key (every key
    // inherits the same shared claim values). Null when there are no keys.
    coLeadClaimSummary() {
      return this.coLeadKeys.length ? this.coLeadKeys[0] : null;
    },
    coLeadTpas() {
      const values = [...new Set(this.coLeadKeys.map((k) => k.tpa).filter((v) => v))];
      return values.length ? values.join(', ') : '—';
    },
    coLeadCoverholders() {
      const values = [...new Set(this.coLeadKeys.map((k) => k.coverholder).filter((v) => v))];
      return values.length ? values.join(', ') : '—';
    },
    coLeadActiveUmrCount() {
      return this.coLeadKeys.length;
    },
    coLeadNonLiqCount() {
      return this.coLeadKeys.filter((k) => k.matched === false).length;
    },
    coLeadExportsCount() {
      return this.coLeadKeys.filter((k) => k.funding_required !== false).length;
    },
    coLeadInsuredNames() {
      const values = [...new Set(this.coLeadKeys.map((k) => k.insured_name).filter((v) => v))];
      return values.length ? values.join(', ') : '—';
    },
    coLeadCauseOfLoss() {
      const first = this.coLeadKeys.map((k) => k.cause_of_loss).find((v) => v);
      return first || '—';
    },
    coLeadLossStates() {
      const values = [...new Set(this.coLeadKeys.map((k) => k.loss_state).filter((v) => v))];
      return values.length ? values.join(', ') : '—';
    },
    hasUploadedDocuments() {
      const docs = this.uploadedDocuments || {};
      return Object.keys(docs).some((key) => Array.isArray(docs[key]) && docs[key].length > 0);
    },
    stepLabels() {
      const labels = ["Initial Questions", "BDX Details", "Claim Details", "Cash Call Details", "Documentation", "Review Summary"];
      if (this.formData.is_co_lead) {
        labels[1] = "UMR Selection & Shares";
      }
      return labels;
    },
    // Both co-lead and single-key end at the 6-step "Review Summary". Co-lead no
    // longer has in-wizard Send / Documents-Produced steps; on "Compile Cash
    // Call" it closes and mirrors the single-key "Email Cash Call Now?" prompt.
    totalSteps() {
      return 6;
    },
    // ---- Step 7 (Co-Lead Send) helpers ----
    coLeadCurrencyPrefix() {
      // Display-only currency symbol. Cash call amounts are USD across the
      // platform (the cash call list table formats with Intl currency:'USD'),
      // so the co-lead send UI / PDF use "$" to match.
      return '$';
    },
    coLeadSendKeyRows() {
      // ALL keys with their per-key amount and funded flag. Amount = share of
      // the full cash call; funding flag decides whether it counts toward
      // Required (mirrors CashCallCoLeadFinancialSummary).
      const total = Number(this.formData.amount) || 0;
      return this.coLeadKeys.map((k) => {
        const sharePercentage = Number(k.share_percentage) || 0;
        return {
          amount: Math.round(total * sharePercentage) / 100,
          funded: k.funding_required !== false
        };
      });
    },
    coLeadSendActiveKeyCount() {
      // All UMRs on the cash call, not just funded ones.
      return this.coLeadSendKeyRows.length;
    },
    coLeadSendTotalAmount() {
      // Full cash call amount = sum of every key's share (funded or not).
      return Math.round(this.coLeadSendKeyRows.reduce((s, r) => s + r.amount, 0) * 100) / 100;
    },
    coLeadSendTotalRequired() {
      // Required = sum of FUNDED key amounts only.
      return Math.round(
        this.coLeadSendKeyRows
          .filter((r) => r.funded)
          .reduce((s, r) => s + r.amount, 0) * 100
      ) / 100;
    },
    brokerDisplay() {
      if (!this.formData || !this.formData.broker) {
        return '—'
      }

      const broker = this.formData.broker

      if (Array.isArray(broker)) {
        return broker.length > 0 ? broker[0] : '—'
      }

      return broker
    }
  },
  watch: {
    // LIQ-1006: on the manual path the BDX key is never typed - it is composed
    // from the six key fields the user fills in on step 2, plus the TPA and
    // Coverholder chosen on step 3. buildManualBdxKey() is the same helper the
    // Co-Lead "Add missing UMR" flow uses, so a manually entered key comes out
    // in Lincoln's own create_new_key() shape (6 or 8 underscore-separated
    // parts). Anything else would fail to match the BDX on reconciliation -
    // the short-vs-long key mismatch that broke the LIQ-1809 export.
    manualBdxKeySource: {
      handler(src) {
        if (!src || this.formData.bdx_key_exists !== false) return
        // Parties first: a unique UMR_RC_CC_SN match fills TPA/Coverholder, and
        // the key is then composed from what that produced rather than from a
        // stale pair.
        this.prefillManualPartiesFromKey()
        this.recomputeManualBdxKey()
        // Then see whether that key + claim ref name a claim we already hold.
        this.prefillManualClaimDetails()
        // Lead UW and Broker come from the key's RLS associations, not the
        // claim, so they need their own lookup.
        this.prefillManualUnderwriterAndBroker()
      },
      deep: true
    },
    // LIQ-1006: the manual claim ref is typed into its own field, but the only
    // thing persisted is `claimref` (the backend reads claimref[0] into
    // CashCallKey.Claim_Reference, and cash_call_reference is built from it).
    // Mirror it across whenever the manual field is in play, otherwise a
    // manually entered claim would save with a null Claim_Reference.
    'formData.claim_ref_manual'(val) {
      if (this.formData.bdx_key_exists === false || this.formData.claim_exists === false) {
        const ref = (val || '').trim()
        this.formData.claimref = ref ? [ref] : []
      }
    },
    'formData.bdx_key_exists'(val) {
      // Switching between the two paths must not leave data behind from the
      // other one. handleSelectedBdxKey() populates umr/rc/cc/sn/currency/YoA
      // by splitting the selected BDX key, so answering "Yes", picking a key,
      // then switching to "No" would otherwise leave those six fields
      // pre-filled with the abandoned key's components - and the manual key
      // would be derived from them (LIQ-1006).
      this.formData.umr = ''
      this.formData.rc = ''
      this.formData.cc = ''
      this.formData.sn = ''
      this.formData.currency_type = ''
      this.formData.year_of_account = 0
      this.formData.bdx_key = []
      this.formData.claimref = []
      this.formData.bdx_key_manual = ''
      this.manualTpaId = null
      this.manualCoverholderId = null

      if (val === false) {
        // Manual path: nothing is selected from the BDX list any more.
        this.claimReferences = []
        // The parties are resolved from BDX/claim data on the normal path -
        // seeded from the opener's props, or fetched by getParameters() for a
        // selected key. With no BDX there is nothing to resolve them from, so
        // clear them and let the user pick: a pre-filled Lead UW the user never
        // chose would otherwise be saved as though they had (LIQ-1006).
        // TPA is left alone: for a TPA-role user it is their own company,
        // stamped server-side and not selectable.
        this.formData.underwriter_lead = ''
        this.formData.coverholder = ''
        this.formData.insured_name = ''
        // Re-mirror the typed claim ref - the manual field keeps its value, so
        // claimref must reflect it rather than the abandoned BDX selection.
        const ref = (this.formData.claim_ref_manual || '').trim()
        this.formData.claimref = ref ? [ref] : []
        this.recomputeManualBdxKey()
      }
    },
    supporting_documents: {
      handler(newVal) {
        if (newVal && newVal.length) {
          this.initializeUploadedDocumentsFromProps();
        }
      },
      immediate: true
    },
    // Switching between the co-lead and normal cash-call flows must NOT carry
    // over Supporting Documentation. The modal is a single instance sharing
    // selectedDocs / uploadedDocuments, so a file attached in one flow was
    // bleeding into the other. Clear both whenever is_co_lead toggles.
    'formData.is_co_lead'(newVal, oldVal) {
      if (newVal === oldVal) return
      // Don't wipe attachments while a saved record is being loaded (is_co_lead
      // flips as the draft loads); only clear on a genuine user flow-switch.
      if (this.isInitializing) return
      this.selectedDocs = [];
      this.uploadedDocuments = {};
      this.otherDescription = '';
    },
    'formData.bdx_key'(newVal, oldVal) {

      // Skip when the change was triggered by a claim selection (claim-first flow)
      if (this.settingBdxFromClaim) return

      // LIQ-1006: on the manual path this watcher must not run at all. The key
      // there is DERIVED from the fields the user is filling in - including TPA
      // and Coverholder, which form its last two segments - so recomputing it
      // fires this watcher, and resetClaimFields() below then wipes the very
      // value that was just entered (TPA/Coverholder/Loss State all cleared the
      // instant they were chosen). Resetting claim data on a key change only
      // makes sense when the key identifies a DIFFERENT BDX record, which is
      // the "Yes" path.
      if (this.formData.bdx_key_exists === false) return

      const newKey = Array.isArray(newVal) && newVal.length ? newVal[0] : null
      const oldKey = Array.isArray(oldVal) && oldVal.length ? oldVal[0] : null

      // Ignore first initialization (modal load)
      if (!oldKey) return

      // If user actually changed BDX
      if (newKey !== oldKey) {
        this.formData.claimref = []
        this.resetClaimFields()
        // TPA users: restore pre-populated claims when BDX is cleared; otherwise wipe them
        if (!newKey && Object.keys(this.claimBdxMapping).length > 0) {
          this.claimReferences = Object.keys(this.claimBdxMapping).map(claimRef => ({
            text: claimRef,
            value: claimRef
          }))
        } else {
          this.claimReferences = []
        }
      }
    },
    'formData.claim_exists'(val) {
      console.log('watch claim_exists', val, 'claimref=', this.formData.claimref)
        if (val === false) {
          //this.formData.claimref = [];
          this.claimReferences = [];
          Vue.prototype.$emitter.emit('claims-loaded', {
          status: true
        })
        return;
        }
        if (val === true) {
          console.log('claim exists answered true')
          console.log(this.formData.bdx_key)
          if (this.formData.bdx_key[0] == 'undefined' || this.formData.bdx_key[0] == undefined) {
            console.log('no bdx selected.')
            // TPA users have claims pre-populated without a BDX key — don't clear them
            if (Object.keys(this.claimBdxMapping).length > 0) {
              return;
            }
            this.claimReferences = [];
            Vue.prototype.$emitter.emit('claims-loaded', {
            status: true
            })
        return;
          }

          // If BDX selected → reload claims
          this.handleSelectedBdxKey(this.formData.bdx_key[0]);
        }
      }
  },
  setup(props, { emit }) {
    //import { getCurrentInstance } from '@vue/composition-api'
    const { useGetters } = createVuexHelpers()
    const { showToast } = useToast()
    const loading = ref(false)
    const savingDraft = ref(false)
    const instance = getCurrentInstance()
    const coLeadUmrCache = ref([])
    const coLeadUmrCacheLoading = ref(false)
    // The flattened + lowercased search index, built ONCE here in the parent
    // (which persists across step navigation) and passed down to the co-lead
    // step. Building it in the child instead meant it was rebuilt over ~400k
    // rows every time the user left step 2 and came back (the child is v-if'd,
    // so destroyed/recreated) - the ~3s Previous->Next lag. markRaw so Vue
    // never deep-observes the index either.
    const coLeadSearchIndex = ref({ rows: [], blobs: [] })


    const { userId } = useCurrentUser()


    // Modal
    const showModal = computed({
      get: () => props.value,
      set: (newValue) => emit('input', newValue)
    })

    const handleHideModal = () => {
      Vue.prototype.$emitter.emit('refresh-cashcall-grid')
      emit('input', false)
    }

    // Persist the current form as a draft, THEN close. Used by the "Closing this
    // window will save your Cash Call as a draft" confirmation - previously that
    // dialog only closed the modal without saving, so co-lead drafts (and any
    // in-progress cash call) closed from an early step were silently lost.
    // Saves as DRAFT for a brand-new record, or keeps the existing status for an
    // already-persisted one (mirrors onSaveAndNext's status choice).
    const saveDraftAndClose = async () => {
      try {
        const proxy = instance && instance.proxy
        if (proxy && typeof proxy.saveForm === 'function') {
          const statusToSave = formData.value.id === 0 ? 'DRAFT' : formData.value.status
          const saved = await proxy.saveForm(statusToSave, null)
          const id = Number((saved && saved.id) || formData.value.id || 0)
          if (id > 0) {
            formData.value.id = id
            // Remember the step the user closed on so reopening the draft (via
            // the pencil) resumes here instead of restarting at step 1.
            const stepToRemember = Math.max(currentStep.value, maxVisitedStep.value || 1)
            maxVisitedStep.value = stepToRemember
            try {
              localStorage.setItem(`cashcall_step_${id}`, stepToRemember)
            } catch (e) { /* localStorage unavailable - non-fatal */ }
          }
        }
      } catch (e) {
        // Even if the save fails, still close (the grid refresh + close below);
        // surfacing a hard error here would trap the user in the modal.
        console.error('Save draft on close failed', e)
      } finally {
        hasChanges.value = false
        handleHideModal()
      }
    }

    const displayflex = 'displayflex'
    const errors = ref([])
    const hasChanges = ref(false)
    const currentStep = ref(1)
    const maxVisitedStep = ref(1)
    const uploadedDocuments = ref({})
    const formContainer = ref(null);

    const filteredRows = ref([])

    const d = 0.0

    const formData = ref({
      id: null,
      status: 'DRAFT',
      claim_loss_established: true,
      claim_reason: '',
      reason_manual: '',
      claim_authority_scope: true,
      is_co_lead: false,
      group_id: null,
      co_lead_keys: [],
      bdx_key_exists: true,
      claim_exists: true,

      //umr: [],

      //BDX Key Fields
      umr: '',
      sn: '',
      rc: '',
      cc: '',
      currency_type: '',
      year_of_account: 0,
      bdx_key: [],
      bdx_key_manual: '',
      claimref: [],
      claim_ref_manual: '',

      //RLS Fields
      tpa: '',
      broker: [],
      new_broker: [],
      new_broker_id: 0,
      coverholder: '',
      underwriter_lead: '',

      // Claim Fields
      insured_name: '',
      date_of_loss: '',
      date_claim_made: '',
      date_risk_effective: '',
      date_risk_expiry: '',
      date_binder_inception: '',
      date_binder_expiry: '',
      date_recent_report_period: '',
      loss_country: 'USA',
      loss_state: '',
      loss_sl_state: '',
      canada_province: '',
      sanction_status: '',
      statutory_deadline: '',
      naic_code: '',
      cause_of_loss: '',

      // Finance Fields
      fee_exists: false,
      amount: '',
      total_fee: '',
      split_fee: '',
      total_indemnity: '',
      expenses_amount: '',
      attorney_coverage_amount: '',
      adjuster_fee_amount: '',
      defence_fees_amount: '',
      tpa_fees_amount: '',
      initial_loss_fund_value: '',
      current_loss_fund_balance: '',

      sl_tax: [],
      date_of_request: '',
      account_id: '',
      upload_doc_type: '',

      account_name: '',
      account_code: '',
      account_type: '',

      cash_call_comments: ''
    })

    // Maps one CashCallKeySerializer object (the shape `keys` arrives in from
    // the backend) into the internal co_lead_keys row shape every Co-Lead
    // component already reads/writes (CashCallCoLeadStep.vue,
    // CashCallCoLeadClaimDetailsStep.vue, CashCallCoLeadLossFundTabs.vue) -
    // several fields are single values in the API (Bdx_Key/Claim_Reference/
    // Broker) but arrays internally, since that's the shape a fresh
    // manual/search-matched addition already uses.
    const mapApiKeyToRow = (item) => ({
      id: item.id,
      bdx_key: item.Bdx_Key ? [item.Bdx_Key] : [],
      umr: item.UMR || '',
      rc: item.Risk_Code || '',
      cc: item.Cat_Code || '',
      sn: item.Section_No || '',
      currency_type: item.Currency_Type || '',
      year_of_account: item.Year_of_Account || 0,
      claimref: item.Claim_Reference ? [item.Claim_Reference] : [],
      tpa: item.TPA || '',
      underwriter_lead: item.Lead_Underwriter || '',
      // Broker is stored comma-joined (a key can carry multiple brokers) -
      // split back into individual entries for the per-row broker chips.
      broker: item.Broker
        ? String(item.Broker).split(',').map((b) => b.trim()).filter((b) => b !== '')
        : [],
      coverholder: item.Coverholder || '',
      insured_name: item.Insured_Name || '',
      date_of_loss: item.Date_of_Loss || '',
      date_claim_made: item.Claim_Date || '',
      loss_country: item.Claim_Country || '',
      loss_state: item.Claim_State || '',
      loss_sl_state: item.SL_Tax_State || '',
      canada_province: item.Claim_Province || '',
      naic_code: item.NAIC_Code || '',
      cause_of_loss: item.Cause_of_Loss || '',
      initial_loss_fund_value: item.Initial_Loss_Fund_Value || 0,
      current_loss_fund_balance: item.Current_Loss_Fund_Balance || 0,
      share_percentage: item.share_percentage,
      funding_required: item.funding_required !== false,
      // Restore the LIQ-vs-Manual flag from the persisted is_limeiq so a
      // manually-added (non-LimeIQ) key isn't silently flipped back to LimeIQ
      // when a saved co-lead cash call is reopened and re-saved. Defaults to
      // true (LimeIQ) for older rows saved before is_limeiq existed.
      matched: item.is_limeiq !== false,
      // Restore the "Underwriter LimeIQ active?" answer so a re-saved co-lead
      // keeps the correct recipient behaviour (No => new underwriter block).
      underwriter_limeiq_active: item.underwriter_limeiq_active !== false,
      // Restore the "Broker in LimeIQ?" state so a re-saved co-lead keeps the
      // new-broker recipient behaviour (manual UMR + new broker => that UMR is
      // un-sendable). Defaults true for older rows.
      broker_limeiq_active: item.broker_limeiq_active !== false
    })

    const initFormData = () => {
      console.log('Inside initFormData')

      // Mark load in progress so the is_co_lead watcher doesn't clear the
      // attachments that get re-hydrated from saved supporting_documents. Reset
      // after Vue has flushed the watchers this load triggers (they run on the
      // next tick), so a later genuine user flow-switch still clears normally.
      const proxyForInit = instance && instance.proxy
      if (proxyForInit) {
        proxyForInit.isInitializing = true
        nextTick(() => { proxyForInit.isInitializing = false })
      }

      formData.value.id = props.id
      formData.value.status = props.status || 'DRAFT'
      formData.value.claim_loss_established = props.claim_loss_established
      formData.value.is_co_lead = !!props.is_co_lead
      formData.value.co_lead_keys = (props.keys || []).map(mapApiKeyToRow)

      const knownReasons = CASH_CALL_REASON_OPTIONS.map(o => o.value).filter(v => v !== 'Other')
      if (props.claim_reason && !knownReasons.includes(props.claim_reason)) {
        formData.value.claim_reason = 'Other'
        formData.value.reason_manual = props.claim_reason
      } else {
        formData.value.claim_reason = props.claim_reason
        formData.value.reason_manual = ''
      }

      formData.value.claim_authority_scope = props.claim_authority_scope,
      formData.value.bdx_key_exists = true,

      //BDX Key Fields
      formData.value.bdx_key = [props.bdx_key] || [],
      formData.value.claimref = [props.claim_ref] || [],
      formData.value.umr = '',
      formData.value.sn = '',
      formData.value.rc = '',
      formData.value.cc = '',
      formData.value.currency_type = '',
      formData.value.year_of_account = 0,      
      formData.value.bdx_key_manual = '',      
      formData.value.claim_ref_manual = '',

      //RLS Fields
      formData.value.tpa = props.tpa || '',
      formData.value.broker = props.broker ? [props.broker] : [],
      formData.value.coverholder = props.coverholder || '',
      formData.value.underwriter_lead = props.underwriter_lead || '',

      // Claim Fields
      formData.value.insured_name = props.insured_name || '',
      formData.value.date_of_loss = props.date_of_loss || '',
      formData.value.date_claim_made = props.date_claim_made || '',
      formData.value.date_risk_effective = props.date_risk_effective || '',
      formData.value.date_risk_expiry = props.date_risk_expiry || '',
      formData.value.date_recent_report_period = props.date_recent_report_period || '',
      formData.value.date_binder_inception = props.date_binder_inception || '',
      formData.value.date_binder_expiry = props.date_binder_expiry || '',
      formData.value.loss_country = props.loss_country || 'USA',
      formData.value.loss_state = props.loss_state || '',
      formData.value.loss_sl_state = props.loss_sl_state || '',
      formData.value.canada_province = props.loss_province || '',
      formData.value.sanction_status = props.sanctions_status || '',
      formData.value.statutory_deadline = props.statutory_deadline || props.Statutory_Deadline || '',
      formData.value.naic_code = props.naic_code || '',
      formData.value.cause_of_loss = '',

      // Finance Fields
      formData.value.fee_exists = props.fee_exists || false
      formData.value.amount = props.amount || '',
      formData.value.total_fee = props.total_fee || '',
      formData.value.split_fee = props.amount || '',
      formData.value.total_indemnity = props.total_indemnity || '',
      formData.value.expenses_amount = props.expenses || '',
      formData.value.attorney_coverage_amount = props.attorney_fee || '',
      formData.value.adjuster_fee_amount = props.adjusters_fee || '',
      formData.value.defence_fees_amount = props.defence_fee || '',
      formData.value.tpa_fees_amount = props.tpa_fee || '',
      formData.value.initial_loss_fund_value = props.initial_loss_fund_value || '',
      formData.value.current_loss_fund_balance = props.current_loss_fund_balance || '',

      formData.value.sl_tax = [],
      formData.value.date_of_request = '',
      formData.value.account_id = '',
      formData.value.upload_doc_type = '',

      formData.value.account_name = '',
      formData.value.account_code = '',
      formData.value.account_type = '',

      formData.value.cash_call_comments = props.cash_call_comments || ''

      // When editing an existing record
      if (formData.value.id && formData.value.id !== 0) {
        if (formData.value.status === 'SAVED') {
          // Fully compiled records — all steps were completed
          maxVisitedStep.value = 6
        } else {
          // Draft — restore the last saved step from localStorage. Clamp to 6:
          // older co-lead drafts may have saved step 7/8 (the removed Send /
          // Documents-Produced steps); those must never be restored now.
          const savedStep = parseInt(localStorage.getItem(`cashcall_step_${formData.value.id}`)) || 1
          maxVisitedStep.value = Math.min(savedStep, 6)
        }
      }

      if (formData.value.bdx_key && formData.value.bdx_key[0]) {
        console.log('calling BDX loader')
        instance.proxy.handleSelectedBdxKey()
      }
    }

    const US_STATES = [
      'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
      'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
      'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
      'VA','WA','WV','WI','WY'
    ]

    const isValidStateCode = () => {
      return US_STATES.includes(this.formData.loss_state)
    }

    const isValidSLStateCode = () => {
      return US_STATES.includes(this.formData.loss_sl_state)
    }

    watch(
        () => formData.value,
        () => {
          hasChanges.value = true
          console.log('hasChanges' + hasChanges.value)
        },
        { deep: true }
    )

    // Fetch the bulk GlobalUMR+claims lookup exactly once and build its search
    // index - reused client-side (faceted filter) for every "Add missing UMR"
    // action instead of re-querying the backend per row. Idempotent: if the
    // cache is already loaded or a load is in flight, it's a no-op, so it's safe
    // to call from multiple triggers (the Co-Lead answer, or opening a co-lead
    // draft).
    let coLeadCacheInFlight = null
    const ensureColeadCache = async () => {
      if (coLeadUmrCache.value.length || coLeadCacheInFlight) return coLeadCacheInFlight
      coLeadUmrCacheLoading.value = true
      coLeadCacheInFlight = (async () => {
        try {
          const response = await CashCallService.getGlobalUMRClaimsBulk()
          // Defensive: the Lincoln-side endpoint this proxies to is a new
          // route that may not be deployed everywhere yet - if it 404s or
          // returns an error object instead of the documented array, fall
          // back to an empty cache (manual entry still works) rather than
          // let a non-array value crash every downstream .flatMap/.filter.
          const bulk = Array.isArray(response.data.data) ? response.data.data : []
          // markRaw: this cache holds ~14k keys carrying ~400k total claim
          // rows. Left reactive, Vue 2 deep-observes every nested object on
          // assignment - seconds of frozen main thread, which is what made
          // typing in the Claim Reference search lag 3-6s per keystroke. We
          // only ever READ this cache (never mutate it in place), so opting
          // it out of reactivity is safe and removes the freeze entirely.
          coLeadUmrCache.value = markRaw(bulk)
          // Build the flattened search index ONCE, here, the moment the cache
          // arrives - so navigating away from and back to step 2 never rebuilds
          // it (that rebuild over ~400k rows was the ~3s Previous->Next lag).
          coLeadSearchIndex.value = markRaw(buildSearchIndex(flattenUmrClaimsCache(bulk)))
          if (!Array.isArray(response.data.data)) {
            console.error('Unexpected bulk GlobalUMR+claims response shape (expected an array):', response.data.data)
          }
        } catch (err) {
          console.error('Error fetching bulk GlobalUMR+claims cache', err)
          coLeadUmrCache.value = []
        } finally {
          coLeadUmrCacheLoading.value = false
          coLeadCacheInFlight = null
        }
      })()
      return coLeadCacheInFlight
    }

    // Kick the fetch off the moment the user answers "Yes" to the Co-Lead
    // question (new cash call).
    watch(
        () => formData.value.is_co_lead,
        (isCoLead) => { if (isCoLead) ensureColeadCache() }
    )


    // Reset hasChanges when modal is closed
    watch(showModal, (newVal) => {
      if (!newVal)
      {
        hasChanges.value = false
      }
      else {
        nextTick(() => {
        console.log("After modal render, resetting step to 1 from " + currentStep.value);
        initFormData()  // reset all fields

        // Decide the step to open on:
        //  - PENDING: jump straight to review/summary (step 6).
        //  - An EXISTING draft/saved record (opened via the pencil): resume at
        //    the last-visited step so the user doesn't have to click through
        //    from step 1 again. initFormData() above already restored
        //    maxVisitedStep from localStorage (clamped to 6).
        //  - A brand-new cash call: start at step 1.
        if (formData.value.id && formData.value.status === 'PENDING') {
          currentStep.value = 6
          maxVisitedStep.value = 6;
        } else if (formData.value.id && formData.value.id !== 0) {
          currentStep.value = Math.min(Math.max(maxVisitedStep.value || 1, 1), 6)
        } else {
          currentStep.value = 1
          maxVisitedStep.value = 1;
        }

        // Clear any stale files first (prevents bleed between a previous open
        // and this one), THEN re-hydrate from the saved supporting_documents so
        // reopening a draft shows the files that were already attached. Without
        // the re-init, this unconditional clear wiped the files that the
        // `supporting_documents` watcher had just loaded, so a reopened draft
        // always showed zero attachments.
        uploadedDocuments.value = {};  // clear all uploaded files
        const proxyRef = instance && instance.proxy
        if (proxyRef && typeof proxyRef.initializeUploadedDocumentsFromProps === 'function') {
          proxyRef.initializeUploadedDocumentsFromProps()
        }
          if (formContainer.value) {
            formContainer.value.scrollTop = 0;
          }

          // Proactively warm the co-lead UMR cache the moment a co-lead cash
          // call opens (e.g. a draft reopened at step 6), so it's ready in the
          // background before the user navigates to step 2 - instead of a
          // multi-second wait when they click "UMR Selection & Shares".
          if (formData.value.is_co_lead) {
            ensureColeadCache()
          }
        });
      }
    })

    // Automatically update total_fee whenever related fee fields change
    watch(
        () => [
          formData.value.tpa_fees_amount,
          formData.value.adjuster_fee_amount,
          formData.value.defence_fees_amount,
          formData.value.expenses_amount,
          formData.value.attorney_coverage_amount
        ],
        ([
           tpa,
           adjuster,
           defence,
           expenses,
           attorney_coverage
         ]) => {
          const toNumber = (v) => parseFloat(String(v).replace(/,/g, '').replace(/\$/g, '')) || 0
          formData.value.total_fee =
              toNumber(tpa) +
              toNumber(adjuster) +
              toNumber(defence) +
              toNumber(expenses) +
              toNumber(attorney_coverage)
        },
        { deep: true }
    )

    // Automatically update total_fee whenever related fee fields change
    watch(
        () => [
          formData.value.tpa_fees_amount,
          formData.value.adjuster_fee_amount,
          formData.value.defence_fees_amount,
          formData.value.expenses_amount,
          formData.value.attorney_coverage_amount,
          formData.value.total_indemnity,
          formData.fee_exists
        ],
        ([
           tpa,
           adjuster,
           defence,
           expenses,
           attorney_coverage,
           indemnity,
           fee_exists
         ]) => {
          const toNumber = (v) => parseFloat(String(v).replace(/,/g, '').replace(/\$/g, '')) || 0

          formData.value.total_indemnity = toNumber(indemnity)
          formData.value.amount =
              toNumber(tpa) +
              toNumber(adjuster) +
              toNumber(defence) +
              toNumber(expenses) +
              toNumber(attorney_coverage) +
              toNumber(indemnity)
        },
        { deep: true }
    )    

    provide(CashCallFormInjectionKey, {
      isModal: props.isModal,
      showModal,
      handleHideModal,
      saveDraftAndClose,

      hasChanges
    })

    return {
      formData,
      formContainer,
      filteredRows,
      errors,
      hasChanges,
      uploadedDocuments,
      currentStep,
      maxVisitedStep,
      displayflex,
      loading,
      savingDraft,
      coLeadUmrCache,
      coLeadSearchIndex,
      coLeadUmrCacheLoading,
      userId,
      US_STATES,
      isValidStateCode,
      isValidSLStateCode
    }
  }
})
</script>

<style scoped>
form {
  display: grid;
  row-gap: 12px;
  /* THE fix for Step 4's loss-fund tab strip pushing the whole form off-screen.
     This grid declares no grid-template-columns, so it gets ONE IMPLICIT column
     whose default sizing function is `auto` == minmax(min-content, max-content).
     An auto track resolves to the MAX-CONTENT width of its widest item, so a
     nowrap row of 8+ UMR tabs (~1715px) sized the track - and with it the form
     and every sibling row, which is why "Current Loss Fund Balance" and
     "Save & Next" were pushed off the right edge too.
     Pinning the track to `minmax(0, 1fr)` makes its min-content floor 0, so the
     track takes the form's own width instead of its content's, and descendants
     with overflow-x:auto can finally scroll inside it.
     NOTE: max-width/min-width on the form BOX (see .form-container > form) can
     never fix this - they bound the box, not the TRACK SIZING FUNCTION that
     computes this width. That distinction is why several CSS-only attempts on
     the strip itself had no effect. */
  grid-template-columns: minmax(0, 1fr);
}

/* Info icon beside a disabled "Save and Next" explaining the share-total gate. */
.save-next-info {
  color: #1c4a50;
  cursor: pointer;
  font-size: 1.05rem;
}
.save-next-info:hover {
  color: #0B5345;
}

form >>> label,
form >>> .col-form-label {
  line-height: 1.2;
  color: black;
}

form .mx-datepicker {
  margin-top: 7px;
}
h1 {
  color: darkcyan;
  text-align: center;
  text-decoration: underline;
  font-size: 20px;
  font-weight: bold;
  padding-bottom: 20px;
  font-family: sans-serif;
}
.form-container {
  /* No fixed/max height and no vertical scrollbar: the container grows to fit
     the step's content so everything shows on one page. (overflow-x stays
     hidden to avoid a stray horizontal scrollbar.) */
  overflow-y: visible;
  overflow-x: hidden;
  padding: 10px;
  border-radius: 2px !important;
  position: relative;
  /* overflow-x:hidden CLIPS overflow but does not stop a child being laid out
     wider than this box: a flex/grid item's automatic minimum size is its
     content width, so a long row (e.g. Step 4's per-UMR loss fund tabs with 8+
     UMRs) sized this container - and through it the modal header, progress bar
     and step indicator - past the viewport, pushing Save & Next off-screen.
     min-width:0 lets the box shrink to its parent so children clip/scroll
     instead of stretching it. */
  min-width: 0;
  max-width: 100%;
}

/* Same reason: the <form> inside form-container was 1024px wide with 1715px of
   content, so every sibling row inherited the wider layout. */
.form-container > form {
  min-width: 0;
  max-width: 100%;
}
/* Co-lead "UMR Selection & Shares" step: no horizontal scrollbar. The step's
   table uses a fixed layout so its columns divide the modal width instead of
   overflowing it, and overflow stays visible so an inline-edit autocomplete
   dropdown can overhang the container instead of being clipped. */
.form-container--hscroll {
  overflow-x: hidden;
}
p {
  color: white;
  display: inline-block !important;
  font-size: 15px;
  font-weight: normal;
  /* font-family: 'Franklin Gothic Medium', 'Arial Narrow', Arial, sans-serif, Courier, monospace 6667AB; */
  font-family: sans-serif;
}
p span {
  background-color: #1c4a50;
  padding: 3px !important;
  border-radius: 3px;
}
.row {
  padding-bottom: 15px !important;
  /* BootstrapVue rows carry negative left/right margins (-15px). Inside the
     padded form container that pushes them past the edge and creates a stray
     horizontal scrollbar. Zero the horizontal margins so rows stay within the
     container width. */
  margin-left: 0 !important;
  margin-right: 0 !important;
}
h4 {
  color: #be394f;
}
.form-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.spinner-lg {
  width: 3rem;
  height: 3rem;
}
.step-indicator {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.step-indicator .step {
  flex: 0 1 auto;
  padding: 6px 4px;
  font-size: 0.82rem;
}
.step {
  background: #e9ecef;
  padding: 6px 14px;
  border-radius: 6px;
  font-weight: 500;
  background: #fff;
  color: gray;
}
.step.active {
  background: #fff;
  color: #1c4a50;
  font-weight: bolder;
  text-decoration: underline;
}
.step.clickable {
  cursor: pointer;
}
.step.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.review-container {
  padding-bottom: 20px;
}

/* Statutory Deadline label (with info icon) on the co-lead Cash Call Details
   step. */
.statutory-deadline-label {
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
}

/* Step 1 Next button shares the row with the "Is this a Co-Lead claim?"
   question; offset it down so it lines up with that question's dropdown
   (clearing the field label above the input). */
.step1-next-col {
  padding-top: 26px;
}


.review-title {
  font-weight: 700;
  font-size: 20px;
  color: #1c4a50;
}

.review-subtitle {
  font-size: 14px;
  color: #666;
}

.review-card {
  border-radius: 10px !important;
  box-shadow: 0px 2px 10px rgba(0,0,0,0.08);
  border: none;
}

.review-card >>> .card-header {
  background: #1c4a50;
  color: white;
  font-weight: 600;
  font-size: 16px;
}

.review-row,
.review-grid > div {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px dashed #ddd;
}

.review-row:last-child,
.review-grid > div:last-child {
  border-bottom: none;
}

.label {
  color: #666;
  font-weight: 500;
}

.value {
  font-weight: 600;
  color: #222;
}

.value.amount {
  color: #be394f;
  font-size: 18px;
  font-weight: 700;
}

.review-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  grid-gap: 10px 20px;
}

.progress-wrapper {
  width: 100%;
}

.progress {
  height: 14px;
  border-radius: 20px;
  background: #e9ecef;
  overflow: hidden;
}

.progress-bar {
  background: linear-gradient(90deg, #1c4a50, #318c93);
  font-size: 11px;
  font-weight: 600;
  color: white;
  text-align: center;
  line-height: 14px;
  transition: width 0.45s ease;
}


.review-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #eee;
}

.review-row:last-child {
  border-bottom: none;
}

.label {
  color: #666;
  font-weight: 500;
}

.value {
  font-weight: 600;
  color: #222;
}

.value.amount {
  color: #be394f;
  font-size: 18px;
  font-weight: 700;
}

.review-title {
  font-size: 20px;
  font-weight: 700;
  color: #1c4a50;
}

.b-card-header {
  background: #1c4a50 !important;
  color: white !important;
  font-weight: 600;
  display: flex;
  align-items: center;
  padding: 10px 14px !important;
}

.b-card {
  border-radius: 10px !important;
  overflow: hidden;
  box-shadow: 0px 3px 10px rgba(0,0,0,0.08);
}

/* ✅ Chevron Icon */
.icon-chevron {
  border: solid white;
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 5px;
  transform: rotate(45deg);
  transition: transform 0.3s ease;
}

/* Rotate when open */
.icon-chevron.rotated {
  transform: rotate(-135deg);
}
/* ✅ Chevron icon */
.chevron {
  border: solid #1c4a50;
  border-width: 0 2px 2px 0;
  padding: 4px;
  display: inline-block;
  transform: rotate(45deg);
  transition: transform 0.25s ease;
}

/* ✅ Rotate when expanded */
.chevron.rotated {
  transform: rotate(-135deg);
}

/* ✅ Animated section expansion */
.animated-section {
  animation: fadeSlideIn 0.35s ease;
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ✅ Animate collapse panel */
.animated-collapse {
  animation: fadeSlide 0.35s ease;
}

@keyframes fadeSlide {
  0% {
    opacity: 0;
    transform: translateY(-6px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ✅ Accordion Header Styling */
.accordion-header {
  background: #f8f9fa !important;
  padding: 10px 14px;
  display: flex;
  align-items: center;
}

.accordion-trigger {
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-grow: 1;
}

.section-title {
  font-weight: 600;
  color: #1c4a50;
  font-size: 15px;
}

/* CO-LEAD review summary: label/value stat card (light grey, rounded) */
.co-lead-summary-card {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
}

.co-lead-summary-card__label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8898aa;
  margin-bottom: 4px;
}

.co-lead-summary-card__value {
  font-weight: 700;
  color: #222;
  word-break: break-word;
}

/* Per-UMR claim card: heading above a two-column grid of label/value rows,
   matching the compact single-key Claim Details look (light grey rounded box,
   muted label left / bold value right). */
.co-lead-claim-card {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
}

.co-lead-claim-card__heading {
  font-weight: 700;
  color: #1c4a50;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e2e8f0;
}

/* Two columns of label/value pairs. Each .review-row keeps its own
   label-left / value-right flex; the grid just places two per line, with a
   column gap between the pairs. */
.co-lead-claim-card__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 40px;
}

/* Inside the two-column grid, drop each row's own bottom divider - the grid
   spacing carries the separation and full-width underlines would misalign
   across the two columns. */
.co-lead-claim-card__grid .review-row {
  border-bottom: none;
}

@media (max-width: 575px) {
  .co-lead-claim-card__grid {
    grid-template-columns: 1fr;
  }
}

/* CO-LEAD review summary: UMR table */
.co-lead-review-table thead th {
  font-size: 11px;
  color: #8898aa;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.co-lead-review-table tbody td {
  font-size: 13px;
  vertical-align: middle;
}

.co-lead-review-table__badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  background-color: #dff3ea;
  color: #2dce89;
}

.co-lead-review-table__badge--no {
  background-color: #fde8ec;
  color: #f5365c;
}

/* Funding-off rows are greyed out, but the badge cell stays vivid. */
.co-lead-review-table__row--no-funding td:not(:last-child) {
  color: #adb5bd;
  background-color: #fafbfc;
}

.edit-btn {
  background: #e2e6ea !important;
  color: #1c4a50 !important;
  font-weight: 600;
}

.doc-list {
  margin-top: 10px;
}

.doc-row {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.doc-label {
  font-weight: 600;
  color: #1c4a50;
}

.uploaded-icon {
  color: green;
  font-size: 18px;
  font-weight: bold;
}

.uploaded-files {
  margin-left: 10px;
}

.uploaded-file-item {
  padding: 4px 0;
  border-bottom: 1px solid #eee;
}

.files-label {
  color:green;
}

.uploading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
}

.is-invalid {
  border-color: #dc3545 !important;
}

.invalid-feedback {
  color: #dc3545;
  font-size: 0.875rem;
  font-weight: 400;
  margin-top: 0.25rem;
}

.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  border: 0.25em solid currentColor;
  border-top: 0.25em solid transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.checkbox-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px; /* row-gap, column-gap */
}

.checkbox-item {
  display: flex;
  align-items: center;
  width: calc(33.33% - 24px); /* 3 per row */
}

.compact-summary {
  background: #fafafa;
  border: 1px solid #ddd;
  font-size: 12px;
}

.summary-table {
  width: 100%;
}

.summary-table th {
  width: 20%;
  text-align: left;
  font-weight: 600;
  padding: 2px 2px;
  background: #f0f0f0;
}

.summary-table td {
  width: 30%;
  padding: 2px 2px;
}

.card {
  margin-bottom:2px !important;
}
.card-body {
  padding: 0.25rem !important;;
}

.modal-title {
  margin: 0;
  line-height: 1.12857143 !important;
}
.modal-body {
  position: relative;
  padding: 9px !important;
}
.progress-wrapper {
  padding-top: 0.5rem !important;
}
.modal-footer {
  display:none !important;
}


</style>

<!-- Non-scoped: the share-total tooltip renders at <body> level (outside this
     component's scope), so its text colour must be styled globally. Green to
     match the Save and Next button. -->
<style>
.tooltip.save-next-tooltip .tooltip-inner {
  color: #1c4a50;
  background-color: #ffffff;
  border: 1px solid #1c4a50;
  font-weight: 500;
}
.tooltip.save-next-tooltip .arrow::before {
  border-top-color: #1c4a50;
  border-bottom-color: #1c4a50;
}
</style>
